<template>
  <a-drawer
    :visible="visible"
    :width="drawerWidth"
    :title="null"
    placement="right"
    @close="handleClose"
    class="participant-detail-drawer"
    :class="{ 'mobile-drawer': drawerWidth === '100%' }"
  >
    <div class="drawer-content-wrapper">
      <!-- 커스텀 헤더 (접기/펼치기 가능) -->
      <div class="detail-header" :class="{ 'header-collapsed': isHeaderCollapsed }">
        <!-- 헤더 요약 (항상 표시) -->
        <div class="header-summary" @click="toggleHeader">
          <div class="summary-left">
            <a-avatar :size="32" class="participant-avatar-small">
              <template #icon><i class="fa fa-user"></i></template>
            </a-avatar>
            <span class="summary-name">{{ participant.customerName || '-' }}</span>
            <a-tag :color="getStatusColor(participant.status)" size="small">
              {{ getStatusText(participant.status) }}
            </a-tag>
          </div>
          <div class="summary-right">
            <a-button
              type="text"
              size="small"
              class="collapse-toggle-btn"
            >
              <i :class="isHeaderCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'"></i>
              {{ isHeaderCollapsed ? '펼치기' : '접기' }}
            </a-button>
          </div>
        </div>

        <!-- 헤더 상세 (접기/펼치기) -->
        <div class="header-detail" v-show="!isHeaderCollapsed">
          <div class="participant-info">
            <div class="main-info">
              <a-avatar :size="48" class="participant-avatar">
                <template #icon><i class="fa fa-user"></i></template>
              </a-avatar>
              <div class="info-text">
                <h2>
                  참여자: {{ participant.customerName || '-' }}
                </h2>
                <div class="id-info">
                  <span class="participant-id">참여자ID: {{ participant._id || participant.id || '-' }}</span>
                  <a-divider type="vertical" />
                  <span class="customer-id">고객ID: {{ participant.customerId || '-' }}</span>
                </div>
                <div class="sub-info">
                  <span><i class="fa fa-user"></i> 고객: {{ participant.customerInfo?.name || '-' }}</span>
                  <a-divider type="vertical" />
                  <span><i class="fa fa-phone"></i> 참여자: {{ participant.customerMobile || '-' }}</span>
                  <a-divider type="vertical" />
                  <span><i class="fa fa-phone-square"></i> 고객: {{ participant.customerInfo?.mobile || '-' }}</span>
                  <a-divider type="vertical" />
                  <span><i class="fa fa-hospital-o"></i> {{ participant.hospital || '-' }}</span>
                </div>
              </div>
            </div>
            <div class="status-info">
              <a-button
                type="primary"
                size="small"
                @click.stop="refreshParticipantData"
                :loading="refreshLoading"
                class="refresh-btn-header"
              >
                <i class="fa fa-refresh"></i> 새로고침
              </a-button>
              <a-tag :color="getStatusColor(participant.status)">
                {{ getStatusText(participant.status) }}
              </a-tag>
              <span class="last-activity">
                마지막 활동: {{ formatRelativeTime(participant.lastActivity) }}
              </span>
            </div>
          </div>
        </div>
      </div>

    <!-- 탭 컨텐츠 -->
    <a-tabs v-model:activeKey="activeTab" class="detail-tabs">
      <!-- 마이페이지 탭 (첫번째로 이동) -->
      <a-tab-pane key="mypage" tab="마이페이지">
        <div class="mypage-container">
          <!-- 헤더 영역 (스크린샷 스타일 참고) -->
          <div class="mypage-header">
            <div class="profile-section">
              <a-avatar :size="64" class="profile-avatar">
                <template #icon><i class="fa fa-user"></i></template>
              </a-avatar>
              <div class="profile-info">
                <h3>{{ myPageData?.name || '-' }}</h3>
                <p>{{ myPageData?.mobile || '-' }}</p>
              </div>
              <a-button
                size="small"
                @click="refreshMyPageData"
                :loading="myPageLoading"
                class="refresh-btn"
              >
                <i class="fa fa-refresh"></i>
              </a-button>
            </div>

            <!-- 빠른 메뉴 버튼들 (스크린샷 스타일) -->
            <div class="quick-menu" v-if="myPageData">
              <div class="menu-item">
                <div class="menu-icon coupon-icon">
                  <i class="fa fa-ticket"></i>
                  <span v-if="totalCouponCount > 0" class="badge">{{ totalCouponCount }}</span>
                </div>
                <span>내 쿠폰함</span>
              </div>
              <div class="menu-item">
                <div class="menu-icon booking-icon">
                  <i class="fa fa-calendar-check-o"></i>
                </div>
                <span>내 예약 정보</span>
              </div>
              <div class="menu-item">
                <div class="menu-icon cert-icon">
                  <i class="fa fa-certificate"></i>
                </div>
                <span>내 인증서</span>
              </div>
            </div>
          </div>

          <a-spin :spinning="myPageLoading">
            <div v-if="myPageData">
              <!-- 기본 정보 섹션 -->
              <a-card title="기본 정보" class="mb-3 info-card">
                <a-descriptions :column="isMobileView ? 1 : 2" bordered size="small">
                  <a-descriptions-item label="이름">{{ myPageData.name || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="전화번호">{{ myPageData.mobile || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="생년월일">{{ myPageData.birth || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="성별">{{ getGenderText(myPageData.gender) }}</a-descriptions-item>
                  <a-descriptions-item label="고객 ID">{{ myPageData._id || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="가입 경로">{{ getOriginSourceText(myPageData.originSource) }}</a-descriptions-item>
                  <a-descriptions-item label="가입일">{{ formatDateTime(myPageData.createAt) }}</a-descriptions-item>
                  <a-descriptions-item label="최종 수정일">{{ formatDateTime(myPageData.modifyAt) }}</a-descriptions-item>
                  <a-descriptions-item label="Klip 주소" :span="isMobileView ? 1 : 2">{{ myPageData.klipAddress || '-' }}</a-descriptions-item>
                </a-descriptions>
              </a-card>

              <!-- 마케팅 동의 정보 -->
              <a-card title="마케팅 동의 정보" class="mb-3 info-card" v-if="myPageData.marketingConsent">
                <a-descriptions :column="isMobileView ? 1 : 2" bordered size="small">
                  <a-descriptions-item label="동의 여부">
                    <a-tag :color="myPageData.marketingConsent.isAgreed ? 'green' : 'red'">
                      {{ myPageData.marketingConsent.isAgreed ? '동의' : '미동의' }}
                    </a-tag>
                  </a-descriptions-item>
                  <a-descriptions-item label="동의 일시">
                    {{ formatDateTime(myPageData.marketingConsent.agreedAt) }}
                  </a-descriptions-item>
                  <a-descriptions-item label="동의 출처">{{ myPageData.marketingConsent.source || '-' }}</a-descriptions-item>
                </a-descriptions>
              </a-card>

              <!-- 반려동물 정보 섹션 -->
              <a-card title="반려동물 정보" class="mb-3 info-card" v-if="myPageData.animalDtos && myPageData.animalDtos.length > 0">
                <div v-for="animal in myPageData.animalDtos" :key="animal.type" class="animal-section">
                  <h5>
                    <i :class="getAnimalIcon(animal.type)"></i>
                    {{ getAnimalTypeText(animal.type) }}
                  </h5>
                  <a-table
                    :columns="animalColumns"
                    :data-source="animal.attributes"
                    :pagination="false"
                    size="small"
                    class="mb-2"
                  >
                    <template #bodyCell="{ column, record }">
                      <template v-if="column.key === 'gender'">
                        {{ getGenderText(record.gender) }}
                      </template>
                      <template v-if="column.key === 'weight'">
                        {{ record.weight }}kg
                      </template>
                    </template>
                  </a-table>
                </div>
              </a-card>

              <!-- 병원 방문 이력 섹션 -->
              <a-card title="병원 방문 이력" class="mb-3 info-card" v-if="myPageData.companyMetadatas && myPageData.companyMetadatas.length > 0">
                <a-table
                  :columns="hospitalColumns"
                  :data-source="formattedHospitalData"
                  :pagination="false"
                  size="small"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'createAt'">
                      {{ formatDateTime(record.createAt) }}
                    </template>
                  </template>
                </a-table>
              </a-card>

              <!-- 미발급 쿠폰 섹션 -->
              <a-card title="미발급 쿠폰" class="mb-3 info-card" v-if="myPageData.unissuedCoupons && myPageData.unissuedCoupons.length > 0">
                <a-badge :count="myPageData.unissuedCoupons.length" :number-style="{ backgroundColor: '#faad14' }" />
                <a-table
                  :columns="unissuedCouponColumns"
                  :data-source="myPageData.unissuedCoupons"
                  :pagination="false"
                  size="small"
                  class="mt-2"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'participatedAt'">
                      {{ formatDateTime(record.participatedAt) }}
                    </template>
                    <template v-if="column.key === 'status'">
                      <a-tag color="orange">{{ getCouponStatusText(record.status) }}</a-tag>
                    </template>
                  </template>
                </a-table>
              </a-card>

              <!-- 발급된 쿠폰 섹션 -->
              <a-card title="발급된 쿠폰" class="mb-3 info-card" v-if="myPageData.issuedCoupons && myPageData.issuedCoupons.length > 0">
                <a-badge :count="myPageData.issuedCoupons.length" :number-style="{ backgroundColor: '#52c41a' }" />
                <a-table
                  :columns="issuedCouponColumns"
                  :data-source="myPageData.issuedCoupons"
                  :pagination="false"
                  size="small"
                  class="mt-2"
                >
                  <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'issuedAt'">
                      {{ formatDateTime(record.issuedAt) }}
                    </template>
                    <template v-if="column.key === 'expiredAt'">
                      {{ formatDate(record.expiredAt) }}
                    </template>
                    <template v-if="column.key === 'status'">
                      <a-tag :color="getCouponStatusColor(record.status)">
                        {{ getCouponStatusText(record.status) }}
                      </a-tag>
                    </template>
                  </template>
                </a-table>
              </a-card>

              <!-- 최신 자이스 NFT 섹션 -->
              <a-card title="최신 자이스 NFT" class="mb-3 info-card" v-if="myPageData.latestZeissNft">
                <a-descriptions :column="isMobileView ? 1 : 2" bordered size="small">
                  <a-descriptions-item label="NFT ID">{{ myPageData.latestZeissNft._id || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="발급 상태">
                    <a-tag :color="myPageData.latestZeissNft.issueStatus === 'issued' ? 'green' : 'orange'">
                      {{ myPageData.latestZeissNft.issueStatus }}
                    </a-tag>
                  </a-descriptions-item>
                  <a-descriptions-item label="NFT 이름">{{ myPageData.latestZeissNft.itemName || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="병원명">{{ myPageData.latestZeissNft.companyName || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="수술일">{{ formatDate(myPageData.latestZeissNft.surgeryAt) }}</a-descriptions-item>
                  <a-descriptions-item label="수술 부위">
                    {{ myPageData.latestZeissNft.surgeryArea?.value || '-' }}
                    ({{ myPageData.latestZeissNft.surgeryArea?.productType || '-' }})
                  </a-descriptions-item>
                  <a-descriptions-item label="총 발행 수">{{ myPageData.latestZeissNft.totalPublishCount?.toLocaleString() || '-' }}번째</a-descriptions-item>
                  <a-descriptions-item label="발급일">{{ formatDateTime(myPageData.latestZeissNft.createAt) }}</a-descriptions-item>
                  <a-descriptions-item label="제조사">{{ myPageData.latestZeissNft.manufacturerName || '-' }}</a-descriptions-item>
                  <a-descriptions-item label="Klip 주소" :span="isMobileView ? 1 : 2">{{ myPageData.latestZeissNft.customerKlipAddress || '-' }}</a-descriptions-item>
                </a-descriptions>
              </a-card>

              <!-- 원본 데이터 표시 -->
              <a-collapse class="mb-3">
                <a-collapse-panel key="1" header="원본 API 응답 데이터">
                  <pre class="json-viewer-simple">{{ JSON.stringify(myPageData, null, 2) }}</pre>
                </a-collapse-panel>
              </a-collapse>
            </div>

            <a-empty v-else-if="!myPageLoading && myPageError" :description="myPageError" />
            <a-empty v-else-if="!myPageLoading" description="상단의 새로고침 버튼을 클릭하여 고객 정보를 조회하세요." />
          </a-spin>
        </div>
      </a-tab-pane>

      <!-- 쿠폰 정보 탭 -->
      <a-tab-pane key="coupon" tab="쿠폰 정보">
        <div class="coupon-container">
          <div class="section-title-with-action">
            <h4 class="section-title">쿠폰 발급 및 사용 현황</h4>
            <a-button
              type="primary"
              size="small"
              class="yellow-button"
              @click="showAddCouponModal"
            >
              <i class="fa fa-plus"></i> 쿠폰 추가
            </a-button>
          </div>
          <a-empty v-if="!participant.couponInfo || participant.couponInfo.length === 0"
            description="발급된 쿠폰이 없습니다." />

          <!-- 모바일: 카드 형태로 표시 -->
          <div v-else-if="isMobileView" class="coupon-card-list">
            <div
              v-for="record in couponDataSource"
              :key="record.key"
              class="coupon-card"
              :class="{ 'coupon-deleted': record.deleteYn === 'Y' }"
            >
              <div class="coupon-card-header">
                <div class="coupon-name">
                  <i class="fa fa-ticket"></i>
                  {{ record.name }}
                </div>
                <div class="coupon-tags">
                  <a-tag :color="getCouponStatusColor(record.status)" size="small">
                    {{ getCouponStatusText(record.status) }}
                  </a-tag>
                  <a-tag v-if="record.deleteYn === 'Y'" color="red" size="small">삭제됨</a-tag>
                </div>
              </div>
              <div class="coupon-card-body">
                <div class="coupon-info-row">
                  <span class="label">쿠폰번호:</span>
                  <span class="value">{{ record.couponNumber || '-' }}</span>
                </div>
                <div class="coupon-info-row">
                  <span class="label">트랜잭션ID:</span>
                  <span class="value">{{ record.couponTransactionId || '-' }}</span>
                </div>
                <div class="coupon-info-row">
                  <span class="label">발급일:</span>
                  <span class="value">{{ record.issuedAt }}</span>
                </div>
                <div class="coupon-info-row">
                  <span class="label">만료일:</span>
                  <span class="value">{{ record.expiredAt }}</span>
                </div>
                <div class="coupon-info-row">
                  <span class="label">사용상태:</span>
                  <span class="value usage-status-cell">
                    <template v-if="couponUsageStatus[record.couponTransactionId]">
                      <a-tag :color="getSendbCouponStatusColor(couponUsageStatus[record.couponTransactionId].status)" size="small">
                        {{ getSendbCouponStatusText(couponUsageStatus[record.couponTransactionId].status) }}
                      </a-tag>
                      <a-tooltip title="다시 조회">
                        <a-button
                          size="small"
                          type="text"
                          class="refresh-btn"
                          :loading="couponStatusLoading[record.couponTransactionId]"
                          @click="() => checkCouponUsageStatus(record)"
                        >
                          <i v-if="!couponStatusLoading[record.couponTransactionId]" class="fa fa-refresh"></i>
                        </a-button>
                      </a-tooltip>
                    </template>
                    <a-button
                      v-else
                      size="small"
                      type="primary"
                      ghost
                      :loading="couponStatusLoading[record.couponTransactionId]"
                      @click="() => checkCouponUsageStatus(record)"
                      :disabled="!record.couponTransactionId"
                    >
                      조회
                    </a-button>
                  </span>
                </div>
                <div v-if="record.addReason" class="coupon-info-row">
                  <span class="label">추가사유:</span>
                  <span class="value">
                    <a-tooltip :title="record.addReason" placement="topLeft">
                      <span class="add-reason-text">
                        {{ record.addReason.length > 20 ? record.addReason.substring(0, 20) + '...' : record.addReason }}
                      </span>
                    </a-tooltip>
                  </span>
                </div>
              </div>
              <div class="coupon-card-footer">
                <template v-if="record.deleteYn !== 'Y'">
                  <a-button
                    v-if="record.status === 'winner_selected'"
                    size="small"
                    type="primary"
                    @click="() => handleResendCoupon(record)"
                    class="yellow-button"
                  >
                    <i class="fa fa-paper-plane"></i> 재발송
                  </a-button>
                  <a-button
                    v-else
                    size="small"
                    disabled
                  >
                    재발송
                  </a-button>
                  <a-tooltip title="쿠폰 회수 (논리적 삭제 - 쿠폰 데이터는 유지하되 사용 불가 처리)">
                    <a-button
                      size="small"
                      type="default"
                      @click="() => handleRevokeCoupon(record)"
                    >
                      <i class="fa fa-undo"></i> 회수
                    </a-button>
                  </a-tooltip>
                  <a-tooltip title="쿠폰 삭제 (완전 삭제 - DB에서 쿠폰 데이터 제거)">
                    <a-button
                      size="small"
                      danger
                      @click="() => handleDeleteCoupon(record)"
                    >
                      <i class="fa fa-trash"></i> 삭제
                    </a-button>
                  </a-tooltip>
                </template>
                <a-button v-else size="small" disabled>
                  삭제된 쿠폰
                </a-button>
              </div>
            </div>
          </div>

          <!-- PC: 기존 테이블 형태 -->
          <a-table
            v-else
            :columns="couponColumns"
            :data-source="couponDataSource"
            :pagination="false"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'status'">
                <a-tag :color="getCouponStatusColor(record.status)">
                  {{ getCouponStatusText(record.status) }}
                </a-tag>
              </template>
              <template v-if="column.key === 'usageStatus'">
                <div class="usage-status-cell">
                  <template v-if="couponUsageStatus[record.couponTransactionId]">
                    <a-tag :color="getSendbCouponStatusColor(couponUsageStatus[record.couponTransactionId].status)" size="small">
                      {{ getSendbCouponStatusText(couponUsageStatus[record.couponTransactionId].status) }}
                    </a-tag>
                    <a-tooltip title="다시 조회">
                      <a-button
                        size="small"
                        type="text"
                        class="refresh-btn"
                        :loading="couponStatusLoading[record.couponTransactionId]"
                        @click="() => checkCouponUsageStatus(record)"
                      >
                        <i v-if="!couponStatusLoading[record.couponTransactionId]" class="fa fa-refresh"></i>
                      </a-button>
                    </a-tooltip>
                  </template>
                  <a-button
                    v-else
                    size="small"
                    type="primary"
                    ghost
                    :loading="couponStatusLoading[record.couponTransactionId]"
                    @click="() => checkCouponUsageStatus(record)"
                    :disabled="!record.couponTransactionId"
                  >
                    조회
                  </a-button>
                </div>
              </template>
              <template v-if="column.key === 'deleteYn'">
                <a-tag v-if="record.deleteYn === 'Y'" color="red">
                  삭제됨
                </a-tag>
                <a-tag v-else color="green">
                  정상
                </a-tag>
              </template>
              <template v-if="column.key === 'addReason'">
                <template v-if="record.addReason">
                  <a-tooltip :title="record.addReason" placement="topLeft">
                    <span class="add-reason-text">
                      {{ record.addReason.length > 15 ? record.addReason.substring(0, 15) + '...' : record.addReason }}
                    </span>
                  </a-tooltip>
                </template>
                <span v-else class="text-muted">-</span>
              </template>
              <template v-if="column.key === 'action'">
                <a-space>
                  <template v-if="record.deleteYn !== 'Y'">
                    <a-tooltip v-if="record.status === 'winner_selected'" title="쿠폰 재발송">
                      <a-button
                        size="small"
                        type="primary"
                        @click="() => handleResendCoupon(record)"
                        class="yellow-button"
                      >
                        재발송
                      </a-button>
                    </a-tooltip>
                    <a-tooltip v-else-if="record.status === 'COUPON_ISSUED'" title="이미 쿠폰을 수령했습니다">
                      <a-button
                        size="small"
                        disabled
                      >
                        재발송
                      </a-button>
                    </a-tooltip>
                    <a-button
                      v-else
                      size="small"
                      disabled
                    >
                      재발송
                    </a-button>
                    <a-tooltip title="쿠폰 회수 (논리적 삭제 - 쿠폰 데이터는 유지하되 사용 불가 처리)">
                      <a-button
                        size="small"
                        type="default"
                        @click="() => handleRevokeCoupon(record)"
                      >
                        <i class="fa fa-undo"></i>
                      </a-button>
                    </a-tooltip>
                    <a-tooltip title="쿠폰 삭제 (완전 삭제 - DB에서 쿠폰 데이터 제거)">
                      <a-button
                        size="small"
                        danger
                        @click="() => handleDeleteCoupon(record)"
                      >
                        <i class="fa fa-trash"></i>
                      </a-button>
                    </a-tooltip>
                  </template>
                  <template v-else>
                    <a-tooltip title="삭제된 쿠폰입니다">
                      <a-button
                        size="small"
                        disabled
                      >
                        삭제됨
                      </a-button>
                    </a-tooltip>
                  </template>
                </a-space>
              </template>
            </template>
          </a-table>
        </div>
      </a-tab-pane>

      <!-- 활동 타임라인 탭 -->
      <a-tab-pane key="timeline" tab="활동 타임라인">
        <div class="timeline-container">
          <h4 class="section-title">
            전체 활동 이력
            <a-badge
              :count="displayActivities.length"
              :number-style="{ backgroundColor: '#1890ff' }"
              style="margin-left: 8px"
            />
            <a-radio-group
              v-model:value="timelineSort"
              size="small"
              class="timeline-sort-toggle"
              button-style="solid"
            >
              <a-radio-button value="asc">
                <i class="fa fa-sort-numeric-asc"></i> 시간순
              </a-radio-button>
              <a-radio-button value="desc">
                <i class="fa fa-sort-numeric-desc"></i> 최신순
              </a-radio-button>
            </a-radio-group>
          </h4>
          <a-spin :spinning="eventMetricsLoading" tip="활동 데이터 로딩중...">
          <a-alert
            v-if="eventMetricsError"
            type="warning"
            :message="eventMetricsError"
            show-icon
            class="mb-3"
          />
          <a-empty v-if="!eventMetricsLoading && (!displayActivities || displayActivities.length === 0)"
            description="기록된 활동이 없습니다." />
          <div v-else class="timeline-wrapper">
            <!-- 최신순일 때 상단에 종료 표시 -->
            <div v-if="timelineSort === 'desc'" class="timeline-end-top">
              <i class="fa fa-flag-checkered"></i> 종료
            </div>

            <!-- 시간순일 때 상단에 시작 표시 -->
            <div v-if="timelineSort === 'asc'" class="timeline-start-top">
              <i class="fa fa-flag"></i> 시작
            </div>

            <a-timeline mode="left">
              <a-timeline-item
                v-for="(activity, index) in displayActivities"
                :key="index"
                :color="getActivityColor(activity.type)"
              >
                <template #dot>
                  <div class="timeline-dot-wrapper">
                    <i :class="getActivityIcon(activity.type)"></i>
                  </div>
                </template>
                <div class="timeline-content">
                  <div class="activity-header">
                    <div class="activity-title-wrapper">
                      <div class="activity-type">{{ activity.type }}</div>
                      <div v-if="activity.details?.btnName" class="activity-btn-name">
                        {{ activity.details.btnName }}
                      </div>
                    </div>
                    <div class="activity-header-right">
                      <span class="activity-time">{{ formatDateTime(activity.timestamp) }}</span>
                      <a-button
                        v-if="activity.details && Object.keys(activity.details).length > 0"
                        size="small"
                        @click="showActivityDetail(activity)"
                        class="activity-detail-btn"
                      >
                        <i class="fa fa-eye"></i> 상세보기
                      </a-button>
                    </div>
                  </div>
                  <div v-if="activity.detail" class="activity-detail">{{ activity.detail }}</div>
                  <div v-if="activity.details" class="activity-details">
                    <a-tag v-if="activity.details.action" size="small" color="blue">
                      액션: {{ activity.details.action }}
                    </a-tag>
                    <a-tag v-if="activity.details.shareChannel && activity.type !== '카카오톡 공유하기'" size="small" color="green">
                      채널: {{ activity.details.shareChannel }}
                    </a-tag>
                    <a-tag v-if="activity.details.eligible !== undefined && activity.type !== '카카오톡 공유하기'" size="small" :color="activity.details.eligible ? 'green' : 'red'">
                      {{ activity.details.eligible ? '응모 완료' : '응모 대상 아님' }}
                    </a-tag>
                  </div>
                  <div v-if="activity.metadata" class="activity-metadata">
                    <a-tag v-for="(value, key) in activity.metadata" :key="key" size="small">
                      {{ key }}: {{ value }}
                    </a-tag>
                  </div>
                </div>
              </a-timeline-item>
            </a-timeline>

            <!-- 시간순일 때 하단에 종료 표시 -->
            <div v-if="timelineSort === 'asc'" class="timeline-end-bottom">
              <i class="fa fa-flag-checkered"></i> 종료
            </div>

            <!-- 최신순일 때 하단에 시작 표시 -->
            <div v-if="timelineSort === 'desc'" class="timeline-start-bottom">
              <i class="fa fa-flag"></i> 시작
            </div>
          </div>
          </a-spin>
        </div>
      </a-tab-pane>

      <!-- 참여 이력 탭 -->
      <a-tab-pane key="history" tab="참여 이력">
        <div class="history-tab-container">
          <div class="section-title-with-badge">
            <h4 class="section-title">전체 이벤트 참여 이력</h4>
            <a-badge
              v-if="attendeeHistory.length > 0"
              :count="attendeeHistory.length"
              :number-style="{ backgroundColor: '#1890ff' }"
            />
          </div>

          <!-- 로딩 상태 -->
          <div v-if="attendeeHistoryLoading" class="history-loading">
            <a-spin tip="참여 이력을 불러오는 중..." />
          </div>

          <!-- 에러 상태 -->
          <a-alert
            v-else-if="attendeeHistoryError"
            :message="attendeeHistoryError"
            type="warning"
            show-icon
          />

          <!-- 데이터 없음 -->
          <a-empty
            v-else-if="attendeeHistory.length === 0"
            description="다른 이벤트 참여 이력이 없습니다."
          />

          <!-- 이력 목록 -->
          <div v-else class="history-list">
            <div
              v-for="(history, index) in attendeeHistory"
              :key="history.attendeeId"
              class="history-item"
              :class="{ 'current-event': history.eventId === eventId }"
            >
              <div class="history-item-header">
                <div class="event-info">
                  <span class="event-title">{{ history.eventTitle || '이벤트명 없음' }}</span>
                  <a-tag
                    v-if="history.eventId === eventId"
                    color="blue"
                    class="current-tag"
                  >
                    현재 이벤트
                  </a-tag>
                </div>
                <span class="registration-date">{{ formatDateTime(history.registeredAt) }}</span>
              </div>

              <div class="history-item-content">
                <div class="info-row">
                  <span class="label">상태:</span>
                  <a-tag :color="getHistoryStatusColor(history.status)">
                    {{ getHistoryStatusText(history.status) }}
                  </a-tag>
                </div>

                <!-- 쿠폰 정보가 있는 경우 -->
                <div v-if="history.couponInfo && history.couponInfo.length > 0" class="coupon-info-section">
                  <div
                    v-for="(coupon, cIdx) in history.couponInfo"
                    :key="cIdx"
                    class="coupon-item"
                  >
                    <div class="info-row">
                      <span class="label">쿠폰:</span>
                      <span class="value">{{ coupon.goodsName || coupon.brand || '-' }}</span>
                    </div>
                    <div class="info-row" v-if="coupon.couponNumber">
                      <span class="label">쿠폰번호:</span>
                      <span class="value coupon-number">{{ coupon.couponNumber }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">쿠폰상태:</span>
                      <a-tag :color="getCouponStatusColor(coupon.status)" size="small">
                        {{ getCouponStatusText(coupon.status) }}
                      </a-tag>
                      <a-tag v-if="coupon.deleteYn === 'Y'" color="red" size="small">삭제됨</a-tag>
                    </div>
                    <div class="info-row" v-if="coupon.couponIssuedAt">
                      <span class="label">발급일:</span>
                      <span class="value">{{ formatDateTime(coupon.couponIssuedAt) }}</span>
                    </div>
                  </div>
                </div>

                <!-- 쿠폰 정보가 없는 경우 -->
                <div v-else class="no-coupon-info">
                  <span class="text-muted">쿠폰 정보 없음</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-tab-pane>

      <!-- 추천 관계 탭 -->
      <a-tab-pane key="referrals" tab="추천 관계">
        <div class="referrals-tab-container">
          <!-- 로딩 상태 -->
          <div v-if="referralsLoading" class="referrals-loading">
            <a-spin tip="추천 관계를 불러오는 중..." />
          </div>

          <!-- 에러 상태 -->
          <a-alert
            v-else-if="referralsError"
            :message="referralsError"
            type="warning"
            show-icon
          />

          <!-- 데이터 표시 -->
          <div v-else-if="referralsData">
            <!-- 추천인 섹션 -->
            <div class="referrals-section">
              <h4 class="section-title">
                <i class="fa fa-user-plus"></i> 나를 추천한 사람 (추천인)
              </h4>
              <a-card v-if="referralsData.referrer" class="referrer-card">
                <div class="referrer-info">
                  <div class="info-row">
                    <span class="label">이름:</span>
                    <span class="value">{{ referralsData.referrer.customerName || '-' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">전화번호:</span>
                    <span class="value">{{ referralsData.referrer.customerMobile || '-' }}</span>
                  </div>
                </div>
              </a-card>
              <a-empty v-else description="추천인 정보가 없습니다." :image-style="{ height: '40px' }" />
            </div>

            <!-- 내가 추천한 사람들 섹션 -->
            <div class="referrals-section mt-4">
              <div class="section-title-with-badge">
                <h4 class="section-title">
                  <i class="fa fa-users"></i> 내가 추천한 사람들
                </h4>
                <a-badge
                  v-if="referralsData.totalReferred > 0"
                  :count="referralsData.totalReferred"
                  :number-style="{ backgroundColor: '#52c41a' }"
                />
              </div>

              <div v-if="referralsData.referredUsers && referralsData.referredUsers.length > 0" class="referred-users-list">
                <a-card
                  v-for="(user, index) in referralsData.referredUsers"
                  :key="user.attendeeId"
                  class="referred-user-card"
                  size="small"
                >
                  <div class="referred-user-info">
                    <div class="user-header">
                      <span class="user-index">#{{ index + 1 }}</span>
                      <span class="user-name">{{ user.customerName || '-' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">전화번호:</span>
                      <span class="value">{{ user.customerMobile || '-' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">등록일:</span>
                      <span class="value">{{ formatDateTime(user.registeredAt) }}</span>
                    </div>
                  </div>
                </a-card>
              </div>
              <a-empty v-else description="추천한 사람이 없습니다." :image-style="{ height: '40px' }" />
            </div>
          </div>

          <!-- 데이터 없음 -->
          <a-empty v-else description="추천 관계 데이터가 없습니다." />
        </div>
      </a-tab-pane>

      <!-- 리뷰 정보 탭 -->
      <a-tab-pane key="reviews" tab="리뷰 정보">
        <div class="review-container">
          <h4 class="section-title">
            리뷰 작성 현황
            <a-badge :count="participant.reviewCount || 0" :number-style="{ backgroundColor: '#52c41a' }" style="margin-left: 8px" />
          </h4>
          <a-empty v-if="!participant.reviews || participant.reviews.length === 0"
            description="작성된 리뷰가 없습니다." />
          <div v-else class="reviews-list">
            <a-card
              v-for="(review, index) in participant.reviews"
              :key="review.reviewId || index"
              class="review-card"
              :bordered="true"
            >
              <div class="review-header">
                <div class="review-info">
                  <a-tag color="blue">{{ review.companyName || review.eventName || '병원명 없음' }}</a-tag>
                  <a-rate :value="review.score || 0" disabled />
                  <span class="review-score">{{ review.score || 0 }}점</span>
                </div>
                <div class="review-date">
                  {{ formatDateTime(review.createdAt) }}
                </div>
              </div>

              <div v-if="review.content" class="review-content">
                <p>{{ review.content }}</p>
              </div>
              <div v-else class="review-content no-content">
                <p><i>리뷰 내용이 없습니다.</i></p>
              </div>

              <div v-if="review.satisfactionPoints && review.satisfactionPoints.length > 0" class="satisfaction-points">
                <div class="points-label">만족 포인트:</div>
                <a-tag v-for="point in review.satisfactionPoints" :key="point" color="green">
                  {{ point }}
                </a-tag>
              </div>

              <div class="review-meta">
                <a-space split="|">
                  <span v-if="review.manufacturerName">
                    <i class="fa fa-industry"></i> {{ review.manufacturerName }}
                  </span>
                  <span v-if="review.nftId">
                    <i class="fa fa-certificate"></i> NFT: {{ review.nftId }}
                  </span>
                  <span v-if="review.updatedAt && review.updatedAt !== review.createdAt">
                    <i class="fa fa-edit"></i> 수정: {{ formatDateTime(review.updatedAt) }}
                  </span>
                </a-space>
              </div>
            </a-card>
          </div>
        </div>
      </a-tab-pane>

      <!-- 메시지 이력 탭 -->
      <a-tab-pane key="messages" tab="메시지 이력">
        <div class="message-container">
          <h4 class="section-title">
            메시지 발송 이력
            <a-radio-group
              v-model:value="messageSort"
              size="small"
              class="timeline-sort-toggle"
              button-style="solid"
            >
              <a-radio-button value="asc">
                <i class="fa fa-sort-numeric-asc"></i> 시간순
              </a-radio-button>
              <a-radio-button value="desc">
                <i class="fa fa-sort-numeric-desc"></i> 최신순
              </a-radio-button>
            </a-radio-group>
          </h4>
          <a-empty v-if="!displayMessages || displayMessages.length === 0"
            description="발송된 메시지가 없습니다." />
          <div v-else class="timeline-wrapper">
            <!-- 최신순일 때 상단에 종료 표시 -->
            <div v-if="messageSort === 'desc'" class="timeline-end-top">
              <i class="fa fa-flag-checkered"></i> 최근
            </div>

            <!-- 시간순일 때 상단에 시작 표시 -->
            <div v-if="messageSort === 'asc'" class="timeline-start-top">
              <i class="fa fa-flag"></i> 시작
            </div>

            <a-timeline mode="left">
              <a-timeline-item
                v-for="(message, index) in displayMessages"
                :key="index"
                :color="getMessageStatusColor(message.status)"
              >
                <template #dot>
                  <div class="timeline-dot-wrapper">
                    <i :class="getMessageStatusIcon(message.status)"></i>
                  </div>
                </template>
                <div class="timeline-content">
                  <div class="activity-header">
                    <div class="activity-title-wrapper">
                      <div class="activity-type">{{ message.type || '메시지 발송' }}</div>
                      <a-tag size="small" :color="getMessageStatusColor(message.status)">
                        {{ getMessageStatusText(message.status) }}
                      </a-tag>
                    </div>
                    <span class="activity-time">{{ formatDateTime(message.sentAt) }}</span>
                  </div>
                  <div class="message-info">
                    <div v-if="message.templateName" class="message-template">
                      <i class="fa fa-file-text-o"></i> 템플릿: {{ message.templateName }}
                    </div>
                    <div v-if="message.content" class="message-content">
                      <div class="message-text">{{ message.content }}</div>
                    </div>
                    <div v-if="message.clickedAt" class="message-interaction">
                      <a-tag size="small" color="green">
                        <i class="fa fa-mouse-pointer"></i> 클릭됨: {{ formatDateTime(message.clickedAt) }}
                      </a-tag>
                    </div>
                    <div v-if="message.viewedAt" class="message-interaction">
                      <a-tag size="small" color="blue">
                        <i class="fa fa-eye"></i> 열람됨: {{ formatDateTime(message.viewedAt) }}
                      </a-tag>
                    </div>
                    <div v-if="message.failedReason" class="message-error">
                      <a-alert
                        :message="`실패 사유: ${message.failedReason}`"
                        type="error"
                        show-icon
                        banner
                      />
                    </div>
                  </div>
                </div>
              </a-timeline-item>
            </a-timeline>

            <!-- 시간순일 때 하단에 종료 표시 -->
            <div v-if="messageSort === 'asc'" class="timeline-end-bottom">
              <i class="fa fa-flag-checkered"></i> 최근
            </div>

            <!-- 최신순일 때 하단에 시작 표시 -->
            <div v-if="messageSort === 'desc'" class="timeline-start-bottom">
              <i class="fa fa-flag"></i> 시작
            </div>
          </div>
        </div>
      </a-tab-pane>

      <!-- 원본 데이터 탭 -->
      <a-tab-pane key="rawdata" tab="원본 데이터">
        <div class="rawdata-container">
          <h4 class="section-title">
            참여자 원본 데이터 (JSON)
            <a-space style="float: right;">
              <a-button
                size="small"
                @click="toggleAllJson"
              >
                <i :class="jsonExpanded ? 'fa fa-compress' : 'fa fa-expand'"></i>
                {{ jsonExpanded ? '모두 접기' : '모두 펼치기' }}
              </a-button>
              <a-button
                size="small"
                @click="copyRawData"
              >
                <i class="fa fa-copy"></i> 복사
              </a-button>
            </a-space>
          </h4>
          <div
            class="json-viewer"
            v-html="formattedJsonHtml"
            @click="handleJsonClick"
          ></div>
        </div>
      </a-tab-pane>

      <!-- SNS 연동 정보 탭 -->
      <a-tab-pane key="sns" tab="SNS 연동">
        <div class="sns-tab-content">
          <div class="sns-search-section">
            <h4 class="section-title">
              <i class="fa fa-link"></i> 로그인 연동 정보 조회
            </h4>
            <div class="sns-search-row">
              <a-input
                v-model:value="snsSearchCustomerId"
                placeholder="고객ID를 입력하세요"
                style="flex: 1"
                @pressEnter="searchCustomerSns"
              />
              <a-button
                type="primary"
                @click="searchCustomerSns"
                :loading="snsSearchLoading"
              >
                <i class="fa fa-search" style="margin-right: 4px"></i>
                조회
              </a-button>
              <a-button
                v-if="participant?.customerId"
                @click="fillCurrentCustomerId"
              >
                현재 고객ID 사용
              </a-button>
            </div>
          </div>

          <!-- 조회 결과 -->
          <div v-if="snsSearchResult !== null" class="sns-result-section">
            <h4 class="section-title">
              <i class="fa fa-list"></i> 조회 결과
              <span class="result-customer-id">({{ snsSearchedCustomerId }})</span>
            </h4>

            <div v-if="snsSearchResult.length === 0" class="sns-empty-result">
              <a-empty description="연동된 SNS 로그인 정보가 없습니다." />
            </div>

            <div v-else class="sns-result-list">
              <a-alert
                type="info"
                :message="`${snsSearchResult.length}개의 SNS 로그인 연동이 있습니다.`"
                show-icon
                style="margin-bottom: 16px"
              />

              <a-table
                :dataSource="snsSearchResult"
                :columns="snsTableColumns"
                :pagination="false"
                size="small"
                rowKey="_id"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'snsType'">
                    <a-tag :color="getSnsTypeColor(record.snsType)">
                      {{ record.snsType }}
                    </a-tag>
                  </template>
                  <template v-if="column.key === 'snsId'">
                    <span class="monospace-text">{{ record.snsId }}</span>
                  </template>
                  <template v-if="column.key === 'createAt'">
                    {{ formatDate(record.createAt) }}
                  </template>
                </template>
              </a-table>
            </div>
          </div>

          <div v-else class="sns-initial-state">
            <a-empty description="고객ID를 입력하고 조회 버튼을 클릭하세요." />
          </div>
        </div>
      </a-tab-pane>
    </a-tabs>
    </div><!-- drawer-content-wrapper -->

    <!-- 활동 상세보기 모달 -->
    <a-modal
      v-model:visible="activityDetailVisible"
      title="활동 상세 정보"
      width="700px"
      :footer="null"
      @cancel="closeActivityDetail"
      :body-style="{ padding: '20px' }"
    >
      <div v-if="selectedActivity" class="activity-detail-modal">
        <div class="detail-section">
          <h4 class="detail-section-title">
            <i class="fa fa-info-circle"></i> 기본 정보
          </h4>
          <a-descriptions :column="2" bordered size="small">
            <a-descriptions-item label="활동 타입">{{ selectedActivity.type }}</a-descriptions-item>
            <a-descriptions-item label="시간">{{ formatDateTime(selectedActivity.timestamp) }}</a-descriptions-item>
            <a-descriptions-item label="점수" v-if="selectedActivity.score !== undefined">{{ selectedActivity.score }}</a-descriptions-item>
            <a-descriptions-item label="점수 타입" v-if="selectedActivity.scoreType">{{ selectedActivity.scoreType }}</a-descriptions-item>
          </a-descriptions>
        </div>

        <div v-if="selectedActivity.details && Object.keys(selectedActivity.details).length > 0" class="detail-section">
          <h4 class="detail-section-title">
            <i class="fa fa-list-alt"></i> 상세 정보 (Details)
          </h4>
          <a-descriptions :column="1" bordered size="small">
            <a-descriptions-item
              v-for="(value, key) in selectedActivity.details"
              :key="key"
              :label="formatDetailKey(key)"
            >
              <template v-if="typeof value === 'boolean'">
                <a-tag :color="value ? 'green' : 'red'">{{ value ? '예' : '아니오' }}</a-tag>
              </template>
              <template v-else-if="key.toLowerCase().includes('timestamp') || key.toLowerCase().includes('at')">
                {{ formatDateTime(value) }}
              </template>
              <template v-else>
                {{ formatDetailValue(value) }}
              </template>
            </a-descriptions-item>
          </a-descriptions>
        </div>

        <div class="detail-section">
          <h4 class="detail-section-title">
            <i class="fa fa-code"></i> 원본 데이터 (JSON)
          </h4>
          <div class="json-viewer-modal">
            <pre>{{ JSON.stringify(selectedActivity, null, 2) }}</pre>
          </div>
          <a-button size="small" @click="copyActivityData" class="copy-btn">
            <i class="fa fa-copy"></i> 복사
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- 쿠폰 추가 모달 -->
    <a-modal
      :visible="addCouponModalVisible"
      title="쿠폰 추가"
      @cancel="closeAddCouponModal"
      :footer="null"
      width="400px"
    >
      <div class="add-coupon-modal-content">
        <p class="modal-description">관리자가 직접 쿠폰번호를 입력하여 쿠폰을 추가합니다.</p>

        <a-form layout="vertical">
          <a-form-item label="상품 선택" required>
            <a-select
              v-model:value="addCouponForm.selectedProductId"
              placeholder="상품을 선택하세요"
              style="width: 100%"
            >
              <a-select-option
                v-for="product in couponProductOptions"
                :key="product.goodsId"
                :value="product.goodsId"
              >
                {{ product.brand }} - {{ product.goodsName }}
              </a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="쿠폰번호" required>
            <a-input
              v-model:value="addCouponForm.couponNumber"
              placeholder="쿠폰번호를 입력하세요"
              @pressEnter="handleAddCoupon"
            />
          </a-form-item>
          <a-form-item label="유효기간 조회">
            <div class="expiry-date-row">
              <a-select
                v-model:value="addCouponForm.lookupType"
                style="width: 100px"
              >
                <a-select-option value="txId">txId</a-select-option>
                <a-select-option value="orderId">orderId</a-select-option>
              </a-select>
              <a-input
                v-model:value="addCouponForm.lookupValue"
                :placeholder="addCouponForm.lookupType === 'txId' ? '예: 25256843' : '예: 260105201126293029'"
                style="flex: 1"
              />
              <a-button
                type="default"
                :loading="expiryDateLoading"
                @click="fetchExpiryDate"
                :disabled="!addCouponForm.lookupValue"
              >
                <i class="fa fa-search"></i> 조회
              </a-button>
            </div>
          </a-form-item>
          <a-form-item label="유효기간">
            <a-date-picker
              v-model:value="addCouponForm.expiryDate"
              placeholder="유효기간 선택 (직접 입력 가능)"
              format="YYYY-MM-DD"
              style="width: 100%"
            />
            <div v-if="fetchedCouponStatus" class="coupon-status-display" :class="getCouponStatusClass(fetchedCouponStatus)">
              <i :class="getCouponStatusIcon(fetchedCouponStatus)"></i>
              {{ getCouponStatusText(fetchedCouponStatus) }}
            </div>
          </a-form-item>
          <a-form-item label="추가 사유 (선택)">
            <a-input
              v-model:value="addCouponForm.reason"
              placeholder="예: 재발급, 누락된 쿠폰 등록"
            />
          </a-form-item>
        </a-form>

        <div class="modal-footer">
          <a-button @click="closeAddCouponModal">취소</a-button>
          <a-button
            type="primary"
            class="yellow-button"
            :loading="addCouponLoading"
            @click="handleAddCoupon"
          >
            추가
          </a-button>
        </div>
      </div>
    </a-modal>
  </a-drawer>
</template>

<script>
import { ref, computed, watch, watchEffect, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';
import { twoBtnDialog } from '@/utils/utils';
import eventsApis from '../../api.js';

dayjs.extend(relativeTime);
dayjs.locale('ko');

export default {
  name: 'ParticipantDetailDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    participant: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'coupon-deleted', 'coupon-resent'],
  setup(props, { emit }) {
    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiMethods = null;
    
    if (instance && instance.proxy) {
      apiMethods = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
    }
    
    const activeTab = ref('mypage');
    const timelineSort = ref('asc'); // 기본값을 시간순(오래된 것 -> 최신)으로 설정
    const messageSort = ref('asc'); // 메시지 정렬 기본값
    const jsonExpanded = ref(true);
    const collapsedPaths = ref(new Set());

    // SNS 연동 조회 관련 상태
    const snsSearchCustomerId = ref('');
    const snsSearchLoading = ref(false);
    const snsSearchResult = ref(null);
    const snsSearchedCustomerId = ref(''); // 조회된 customerId 표시용

    // SNS 테이블 컬럼 정의
    const snsTableColumns = [
      { title: 'SNS 타입', key: 'snsType', dataIndex: 'snsType', width: 100 },
      { title: 'SNS ID', key: 'snsId', dataIndex: 'snsId' },
      { title: '연동일시', key: 'createAt', dataIndex: 'createAt', width: 180 }
    ];

    // 헤더 접기/펼치기 상태 (모바일에서 기본 접힘)
    const isHeaderCollapsed = ref(false);
    const toggleHeader = () => {
      isHeaderCollapsed.value = !isHeaderCollapsed.value;
    };

    // 반응형 드로워 너비
    const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);
    const drawerWidth = computed(() => {
      if (windowWidth.value < 768) {
        return '100%'; // 모바일: 전체 화면
      } else if (windowWidth.value < 1024) {
        return '90%'; // 태블릿: 90%
      }
      return 1200; // 데스크톱
    });

    // 모바일 뷰 감지
    const isMobileView = computed(() => windowWidth.value < 768);

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });

    // 활동 상세보기 모달 관련 상태
    const activityDetailVisible = ref(false);
    const selectedActivity = ref(null);

    // 쿠폰 추가 모달 관련 상태
    const addCouponModalVisible = ref(false);
    const addCouponLoading = ref(false);
    const expiryDateLoading = ref(false);
    const fetchedCouponStatus = ref(null);  // 조회된 쿠폰 상태 (화면 표시용)

    // 쿠폰 사용상태 조회 관련 상태 (각 쿠폰별로 저장)
    const couponUsageStatus = ref({});  // { couponNumber: { status, loading, error, data } }
    const couponStatusLoading = ref({});  // { couponNumber: boolean }
    const addCouponForm = ref({
      selectedProductId: null,
      couponNumber: '',
      reason: '',
      lookupType: 'txId',
      lookupValue: '',
      expiryDate: null
    });

    // 쿠폰 상품 옵션 (관리자 수동 추가 시 선택 가능한 상품 목록)
    const couponProductOptions = ref([
      {
        goodsId: 'G00000201098',
        goodsName: '아메리카노(Ice)(TAKE-OUT)',
        goodsImg: 'https://img.giftting.co.kr/goods/G00000201098/G00000201098_250.jpg',
        brand: '컴포즈커피',
        brandCategory: '커피/음료',
        category1: '실물',
        category2: '기타',
        orgPrice: 1800,
        salePrice: 1440,
      },
    ]);

    // 마이페이지 관련 상태
    const myPageLoading = ref(false);
    const myPageData = ref(null);
    const myPageError = ref(null);

    // 새로고침 관련 상태
    const refreshLoading = ref(false);

    // eventmetrics 관련 상태
    const eventMetrics = ref([]);
    const eventMetricsLoading = ref(false);
    const eventMetricsError = ref(null);

    // 참여 이력 관련 상태
    const attendeeHistory = ref([]);
    const attendeeHistoryLoading = ref(false);
    const attendeeHistoryError = ref(null);

    // 추천 관계 관련 상태
    const referralsData = ref(null);
    const referralsLoading = ref(false);
    const referralsError = ref(null);

    // 현재 이벤트 ID (computed)
    const eventId = computed(() => props.participant?.eventId || null);

    // 참여 이력 상태 색상
    const getHistoryStatusColor = (status) => {
      const statusColors = {
        'registered': 'blue',
        'winner_selected': 'cyan',
        'coupon_issued': 'green',
        'coupon_used': 'purple',
        'rejected': 'red',
        'blocked': 'orange'
      };
      return statusColors[status] || 'default';
    };

    // 참여 이력 상태 텍스트
    const getHistoryStatusText = (status) => {
      const statusTexts = {
        'registered': '등록완료',
        'winner_selected': '당첨선정',
        'coupon_issued': '쿠폰발급',
        'coupon_used': '쿠폰사용',
        'rejected': '거부됨',
        'blocked': '차단됨'
      };
      return statusTexts[status] || status || '알 수 없음';
    };

    // 드로어가 열릴 때 마이페이지 데이터 및 eventmetrics 자동 로드
    watch(() => props.visible, (newVal) => {
      if (newVal && props.participant?.customerId) {
        // 드로어가 열리고 고객 ID가 있으면 마이페이지 데이터 로드
        loadMyPageData();
        // eventmetrics 로드 (attendeeId가 있는 경우)
        if (props.participant?._id) {
          loadEventMetrics();
        }
        // 참여 이력 로드 (전화번호가 있는 경우)
        if (props.participant?.customerMobile) {
          loadAttendeeHistory();
        }
        // 추천 관계 로드 (attendeeId가 있는 경우)
        if (props.participant?._id) {
          loadReferrals();
        }
      } else if (!newVal) {
        // 드로어가 닫히면 데이터 초기화
        myPageData.value = null;
        myPageError.value = null;
        eventMetrics.value = [];
        eventMetricsError.value = null;
        attendeeHistory.value = [];
        attendeeHistoryError.value = null;
        referralsData.value = null;
        referralsError.value = null;
      }
    });

    // eventmetrics 로드 함수
    const loadEventMetrics = async () => {
      if (!apiMethods || !props.participant?.eventId || !props.participant?._id) {
        console.log('eventmetrics 로드 스킵: API 또는 필수 정보 없음');
        return;
      }

      eventMetricsLoading.value = true;
      eventMetricsError.value = null;

      try {
        await apiMethods.getAttendeeEventMetrics(
          props.participant.eventId,
          props.participant._id,
          (response) => {
            if (response.data?.success && response.data?.data) {
              eventMetrics.value = response.data.data;
              eventMetricsError.value = null; // 성공 시 에러 클리어
              console.log('eventmetrics 로드 성공:', eventMetrics.value.length, '개');
            } else {
              eventMetrics.value = [];
            }
          },
          (error) => {
            console.error('eventmetrics 로드 실패:', error);
            eventMetricsError.value = '활동 데이터를 불러오는 데 실패했습니다.';
            eventMetrics.value = [];
          }
        );
      } catch (error) {
        console.error('eventmetrics 로드 예외:', error);
        eventMetricsError.value = '활동 데이터를 불러오는 데 실패했습니다.';
        eventMetrics.value = [];
      } finally {
        eventMetricsLoading.value = false;
      }
    };

    // 참여 이력 로드 함수
    const loadAttendeeHistory = async () => {
      if (!apiMethods || !props.participant?.customerMobile) {
        console.log('참여 이력 로드 스킵: API 또는 전화번호 없음');
        return;
      }

      attendeeHistoryLoading.value = true;
      attendeeHistoryError.value = null;

      try {
        await apiMethods.getAttendeeHistory(
          props.participant.customerMobile,
          (response) => {
            if (response.data?.success && response.data?.data) {
              attendeeHistory.value = response.data.data.history || [];
              console.log('참여 이력 로드 성공:', attendeeHistory.value.length, '개 이벤트');
            } else {
              attendeeHistory.value = [];
            }
          },
          (error) => {
            console.error('참여 이력 로드 실패:', error);
            attendeeHistoryError.value = '참여 이력을 불러오는 데 실패했습니다.';
            attendeeHistory.value = [];
          }
        );
      } catch (error) {
        console.error('참여 이력 로드 예외:', error);
        attendeeHistoryError.value = '참여 이력을 불러오는 데 실패했습니다.';
        attendeeHistory.value = [];
      } finally {
        attendeeHistoryLoading.value = false;
      }
    };

    // 추천 관계 로드 함수
    const loadReferrals = async () => {
      if (!apiMethods || !props.participant?.eventId || !props.participant?._id) {
        console.log('추천 관계 로드 스킵: API 또는 필수 정보 없음');
        return;
      }

      referralsLoading.value = true;
      referralsError.value = null;

      try {
        await apiMethods.getAttendeeReferrals(
          props.participant.eventId,
          props.participant._id,
          (response) => {
            if (response.data?.success && response.data?.data) {
              referralsData.value = response.data.data;
              console.log('추천 관계 로드 성공:', referralsData.value);
            } else {
              referralsData.value = null;
            }
          },
          (error) => {
            console.error('추천 관계 로드 실패:', error);
            referralsError.value = '추천 관계를 불러오는 데 실패했습니다.';
            referralsData.value = null;
          }
        );
      } catch (error) {
        console.error('추천 관계 로드 예외:', error);
        referralsError.value = '추천 관계를 불러오는 데 실패했습니다.';
        referralsData.value = null;
      } finally {
        referralsLoading.value = false;
      }
    };

    // 메트릭 계산
    const metrics = computed(() => {
      const p = props.participant;
      return {
        visitRate: p.visited ? 100 : 0,
        shareRate: p.sharedAt ? 60 : 0,
        participationRate: p.couponReceived ? 30 : 0,
        completionRate: p.loggedIn ? 10 : 0
      };
    });
    
    // 현재 단계 계산
    const getCurrentStep = () => {
      const p = props.participant;
      if (p.loggedIn) return 3;
      if (p.couponReceived) return 2;
      if (p.sharedAt) return 1;
      return 0;
    };
    
    // 단계 진행률 계산
    const getStepPercent = () => {
      const step = getCurrentStep();
      return (step + 1) * 25;
    };
    
    // 친구 참여 수
    const friendParticipationCount = computed(() => {
      return props.participant.friendParticipation || 0;
    });
    
    // 쿠폰 사용/전체 수
    const couponUsedCount = computed(() => {
      if (!props.participant.couponInfo) return 0;
      return props.participant.couponInfo.filter(c => c.status === 'used').length;
    });
    
    const couponTotalCount = computed(() => {
      return props.participant.couponInfo?.length || 0;
    });
    
    // 활동 점수 계산
    const activityScore = computed(() => {
      let score = 0;
      const p = props.participant;
      
      // 접속: 10점
      if (p.visited) score += 10;
      
      // 공유: 회당 5점 (최대 50점)
      const shareScore = Math.min((p.shareCount || 0) * 5, 50);
      score += shareScore;
      
      // 친구 참여: 인당 10점
      score += (p.friendParticipation || 0) * 10;
      
      // 쿠폰 사용: 개당 20점
      score += couponUsedCount.value * 20;
      
      return score;
    });
    
    // 포맷된 JSON HTML (접기/펼치기 가능)
    const formattedJsonHtml = computed(() => {
      if (!props.participant) return '<span>{}</span>';
      
      let pathCounter = 0;
      let lineNumber = 1;
      
      const formatValue = (obj, indent = 0, path = '', isLast = true) => {
        if (obj === null) return '<span class="json-null">null</span>';
        if (obj === undefined) return '<span class="json-undefined">undefined</span>';
        
        const type = typeof obj;
        
        if (type === 'string') {
          // 문자열 이스케이프 처리
          const escaped = obj.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          return `<span class="json-string">"${escaped}"</span>`;
        }
        if (type === 'number') {
          return `<span class="json-number">${obj}</span>`;
        }
        if (type === 'boolean') {
          return `<span class="json-boolean">${obj}</span>`;
        }
        
        if (Array.isArray(obj)) {
          if (obj.length === 0) return '<span class="json-bracket">[]</span>';
          
          const currentPath = `path-${pathCounter++}`;
          const isCollapsed = collapsedPaths.value.has(currentPath);
          const spaces = ' '.repeat(indent * 2);
          const innerSpaces = ' '.repeat((indent + 1) * 2);
          
          let result = '';
          
          // 토글 버튼과 여는 브래킷
          result += `<span class="json-toggle" data-path="${currentPath}">${isCollapsed ? '▶' : '▼'}</span>`;
          result += `<span class="json-bracket">[</span>`;
          
          if (isCollapsed) {
            result += `<span class="json-ellipsis">...</span><span class="json-bracket">]</span>`;
          } else {
            result += '\n';
            obj.forEach((item, index) => {
              const isLastItem = index === obj.length - 1;
              result += innerSpaces + formatValue(item, indent + 1, currentPath, isLastItem);
              if (!isLastItem) result += ',';
              result += '\n';
            });
            result += spaces + '<span class="json-bracket">]</span>';
          }
          
          return result;
        }
        
        if (type === 'object') {
          const keys = Object.keys(obj);
          if (keys.length === 0) return '<span class="json-bracket">{}</span>';
          
          const currentPath = `path-${pathCounter++}`;
          const isCollapsed = collapsedPaths.value.has(currentPath);
          const spaces = ' '.repeat(indent * 2);
          const innerSpaces = ' '.repeat((indent + 1) * 2);
          
          let result = '';
          
          // 토글 버튼과 여는 브래킷
          result += `<span class="json-toggle" data-path="${currentPath}">${isCollapsed ? '▶' : '▼'}</span>`;
          result += `<span class="json-bracket">{</span>`;
          
          if (isCollapsed) {
            result += `<span class="json-ellipsis">...</span><span class="json-bracket">}</span>`;
          } else {
            result += '\n';
            keys.forEach((key, index) => {
              const isLastItem = index === keys.length - 1;
              const value = obj[key];
              const isExpandable = value !== null && typeof value === 'object';
              
              result += innerSpaces;
              result += `<span class="json-key">"${key}"</span>: `;
              result += formatValue(value, indent + 1, currentPath, isLastItem);
              if (!isLastItem) result += ',';
              result += '\n';
            });
            result += spaces + '<span class="json-bracket">}</span>';
          }
          
          return result;
        }
        
        return String(obj);
      };
      
      return `<pre>${formatValue(props.participant)}</pre>`;
    });
    
    // 쿠폰 테이블 컬럼
    const couponColumns = [
      {
        title: '쿠폰명',
        dataIndex: 'name',
        key: 'name',
        width: '14%'
      },
      {
        title: '쿠폰번호',
        dataIndex: 'couponNumber',
        key: 'couponNumber',
        width: '12%'
      },
      {
        title: '트랜잭션ID',
        dataIndex: 'couponTransactionId',
        key: 'couponTransactionId',
        width: '10%'
      },
      {
        title: '발급일',
        dataIndex: 'issuedAt',
        key: 'issuedAt',
        width: '10%'
      },
      {
        title: '만료일',
        dataIndex: 'expiredAt',
        key: 'expiredAt',
        width: '10%'
      },
      {
        title: '상태',
        key: 'status',
        width: '8%'
      },
      {
        title: '사용상태',
        key: 'usageStatus',
        width: '10%',
        align: 'center'
      },
      {
        title: '삭제여부',
        key: 'deleteYn',
        width: '6%'
      },
      {
        title: '추가사유',
        key: 'addReason',
        dataIndex: 'addReason',
        width: '10%',
        ellipsis: true
      },
      {
        title: '액션',
        key: 'action',
        width: '10%',
        align: 'center'
      }
    ];
    
    // 쿠폰 데이터 소스
    const couponDataSource = computed(() => {
      if (!props.participant.couponInfo) return [];

      return props.participant.couponInfo.map((coupon, index) => {
        // 실제 API 상태값을 그대로 사용
        return {
          key: index,
          name: coupon.goodsInfo?.goodsName || coupon.goodsName || '스타벅스 아메리카노',
          couponNumber: coupon.couponNumber || '-',
          couponTransactionId: coupon.couponTransactionId || null, // SendB 트랜잭션 ID
          couponOrderId: coupon.couponOrderId || null, // SendB 주문 ID
          issuedAt: formatDate(coupon.couponIssuedAt || coupon.createdAt),
          expiredAt: formatDate(coupon.couponExpiryDate || coupon.goodsInfo?.limitDate),
          status: coupon.status, // winner_selected 또는 COUPON_ISSUED
          originalStatus: coupon.status,
          deleteYn: coupon.deleteYn || 'N', // 삭제 여부 추가 (Y/N)
          addReason: coupon.addReason || '', // 수동 추가 사유
          ...coupon
        };
      });
    });
    
    // 정렬된 활동 목록 (eventmetrics 컬렉션 기준으로 변경)
    const displayActivities = computed(() => {
      // eventmetrics 데이터 사용 (우선순위)
      if (eventMetrics.value && eventMetrics.value.length > 0) {
        const activities = [...eventMetrics.value].map(metric => ({
          ...metric,
          // eventmetrics 필드를 활동 타임라인 형식으로 매핑
          type: metric.legacyType || metric.event || 'UNKNOWN',
          timestamp: metric.timestamp,
          details: {
            page: metric.page,
            event: metric.event,
            target: metric.target,
            result: metric.result,
            ...(metric.metadata || {})
          },
          detail: getEventMetricDetail(metric),
          metadata: metric.metadata
        }));

        // 정렬 방향에 따라 정렬
        if (timelineSort.value === 'desc') {
          return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        } else {
          return activities.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        }
      }

      // fallback: 기존 activities 배열 사용
      if (!props.participant.activities) return [];

      const activities = [...props.participant.activities].map(activity => ({
        ...activity,
        details: activity.details || {},
        detail: getActivityDetail(activity),
        metadata: getActivityMetadata(activity)
      }));

      // 정렬 방향에 따라 정렬
      if (timelineSort.value === 'desc') {
        // 최신순 (최근 -> 과거)
        return activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      } else {
        // 시간순 (과거 -> 최근)
        return activities.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      }
    });

    // eventmetrics 상세 정보 생성 함수
    const getEventMetricDetail = (metric) => {
      const parts = [];
      if (metric.page) parts.push(`페이지: ${metric.page}`);
      if (metric.event) parts.push(`이벤트: ${metric.event}`);
      if (metric.target) parts.push(`대상: ${metric.target}`);
      return parts.join(' | ');
    };
    
    // 정렬된 메시지 목록
    const displayMessages = computed(() => {
      if (!props.participant.messageSendHistory || props.participant.messageSendHistory.length === 0) {
        return [];
      }
      
      const messages = [...props.participant.messageSendHistory];
      
      // 정렬 방향에 따라 정렬
      if (messageSort.value === 'desc') {
        // 최신순 (최근 -> 과거)
        return messages.sort((a, b) => new Date(b.sentAt || b.createdAt) - new Date(a.sentAt || a.createdAt));
      } else {
        // 시간순 (과거 -> 최근)
        return messages.sort((a, b) => new Date(a.sentAt || a.createdAt) - new Date(b.sentAt || b.createdAt));
      }
    });
    
    // 메시지 통계 계산
    const messageStats = computed(() => {
      if (!props.participant.messageSendHistory || props.participant.messageSendHistory.length === 0) {
        return null;
      }
      
      const messages = props.participant.messageSendHistory;
      return {
        total: messages.length,
        sent: messages.filter(m => m.status === 'sent' || m.status === 'success').length,
        viewed: messages.filter(m => m.status === 'viewed' || m.viewedAt).length,
        clicked: messages.filter(m => m.status === 'clicked' || m.clickedAt).length,
        failed: messages.filter(m => m.status === 'failed').length
      };
    });
    
    // Helper 함수들
    const handleClose = () => {
      emit('close');
    };
    
    const getStatusColor = (status) => {
      const colors = {
        participated: 'default',
        shared: 'blue',
        coupon_pending: 'orange',
        coupon_received: 'green',
        logged_in: 'purple'
      };
      return colors[status] || 'default';
    };
    
    const getStatusText = (status) => {
      const texts = {
        participated: '참여',
        shared: '공유 완료',
        coupon_pending: '쿠폰 수령 대기',
        coupon_received: '쿠폰 수령 완료',
        logged_in: '로그인 완료'
      };
      return texts[status] || status;
    };
    
    const formatRelativeTime = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).fromNow();
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD');
    };
    
    const formatDateTime = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD HH:mm:ss');
    };

    // SNS 타입별 색상
    const getSnsTypeColor = (snsType) => {
      const colors = {
        kakao: 'gold',
        apple: 'default',
        wechat: 'green',
        KAKAO: 'gold',
        APPLE: 'default',
        WECHAT: 'green'
      };
      return colors[snsType] || 'blue';
    };

    // 현재 참여자의 customerId로 자동 채우기
    const fillCurrentCustomerId = () => {
      if (props.participant?.customerId) {
        snsSearchCustomerId.value = props.participant.customerId;
      }
    };

    // SNS 연동 정보 조회
    const searchCustomerSns = async () => {
      const customerId = snsSearchCustomerId.value.trim();
      if (!customerId) {
        message.warning('고객ID를 입력해주세요.');
        return;
      }

      snsSearchLoading.value = true;
      snsSearchResult.value = null;

      try {
        await new Promise((resolve, reject) => {
          apiMethods.getCustomerSnsByCustomerId(
            { customerId },
            (response) => {
              const data = response.data?.data || response.data || [];
              snsSearchResult.value = Array.isArray(data) ? data : [];
              snsSearchedCustomerId.value = customerId;
              resolve(response);
            },
            (error) => {
              console.error('SNS 연동 조회 실패:', error);
              message.error('SNS 연동 정보 조회 중 오류가 발생했습니다.');
              snsSearchResult.value = [];
              snsSearchedCustomerId.value = customerId;
              reject(error);
            }
          );
        });
      } catch (error) {
        console.error('SNS 연동 조회 오류:', error);
      } finally {
        snsSearchLoading.value = false;
      }
    };

    const getAvatarColor = (status) => {
      return status === 'completed' ? '#52c41a' : '#faad14';
    };
    
    const getCouponStatusType = (status) => {
      const types = {
        pending: 'warning',
        issued: 'processing',
        used: 'success',
        expired: 'error'
      };
      return types[status] || 'default';
    };
    
    const getCouponStatusText = (status) => {
      const texts = {
        'winner_selected': '수령 대기',
        'COUPON_ISSUED': '수령 완료',
        'used': '사용 완료',
        'expired': '만료',
        // SendB cpnStatus
        '0': '전송전',
        'B': '사용가능',
        'C': '사용완료',
        'J': '기간만료',
        'H': '취소됨',
        'F': '발송실패'
      };
      return texts[status] || status;
    };

    // 쿠폰 상태에 따른 CSS 클래스
    const getCouponStatusClass = (status) => {
      const classes = {
        '0': 'status-pending',
        'B': 'status-available',
        'C': 'status-used',
        'J': 'status-expired',
        'H': 'status-cancelled',
        'F': 'status-failed'
      };
      return classes[status] || '';
    };

    // 쿠폰 상태에 따른 아이콘
    const getCouponStatusIcon = (status) => {
      const icons = {
        '0': 'fa fa-clock-o',
        'B': 'fa fa-check-circle',
        'C': 'fa fa-gift',
        'J': 'fa fa-calendar-times-o',
        'H': 'fa fa-ban',
        'F': 'fa fa-exclamation-triangle'
      };
      return icons[status] || 'fa fa-question-circle';
    };
    
    const getCouponStatusColor = (status) => {
      const colors = {
        'winner_selected': 'orange',
        'COUPON_ISSUED': 'green',
        'used': 'blue',
        'expired': 'red'
      };
      return colors[status] || 'default';
    };
    
    const getActivityColor = (type) => {
      const colors = {
        '카카오톡 공유하기': '#fee500',
        '링크 복사': 'green',
        '쿠폰 수령': 'blue',
        '로그인': 'purple',
        '이벤트 참여': 'orange'
      };
      return colors[type] || 'gray';
    };
    
    const getActivityIcon = (type) => {
      const icons = {
        '카카오톡 공유하기': 'fa fa-comment',
        '링크 복사': 'fa fa-link',
        '쿠폰 수령': 'fa fa-ticket',
        '로그인': 'fa fa-sign-in',
        '이벤트 참여': 'fa fa-user-plus'
      };
      return icons[type] || 'fa fa-circle';
    };
    
    const getActivityDetail = (activity) => {
      // 활동 타입에 따른 상세 설명 생성
      if (activity.type === '카카오톡 공유하기') {
        return '카카오톡으로 이벤트를 공유했습니다.';
      }
      if (activity.type === '쿠폰 수령') {
        return '이벤트 쿠폰을 수령했습니다.';
      }
      return null;
    };
    
    const getActivityMetadata = (activity) => {
      // 활동 타입에 따른 메타데이터 생성
      if (activity.metadata) {
        return activity.metadata;
      }
      return null;
    };
    
    // 메시지 상태별 색상
    const getMessageStatusColor = (status) => {
      const colors = {
        requested: 'orange',
        sent: 'green',
        viewed: 'blue',
        clicked: 'purple',
        failed: 'red'
      };
      return colors[status] || 'default';
    };
    
    // 메시지 상태별 텍스트
    const getMessageStatusText = (status) => {
      const texts = {
        requested: '발송 대기',
        sent: '발송 완료',
        viewed: '열람됨',
        clicked: '클릭됨',
        failed: '발송 실패'
      };
      return texts[status] || status;
    };
    
    // 메시지 상태별 아이콘
    const getMessageStatusIcon = (status) => {
      const icons = {
        requested: 'fa fa-clock-o',
        sent: 'fa fa-check',
        viewed: 'fa fa-eye',
        clicked: 'fa fa-mouse-pointer',
        failed: 'fa fa-exclamation-circle'
      };
      return icons[status] || 'fa fa-envelope';
    };
    
    const handleSendCoupon = (coupon) => {
      message.info(`쿠폰 발송 기능 준비중입니다.`);
      // API 호출 로직 추가 - 쿠폰 번호 생성 및 발송
    };
    
    const handleResendCoupon = (coupon) => {
      // 참여자 ID와 이벤트 ID 확인
      const attendeeId = props.participant._id || props.participant.id;
      const eventId = props.participant.eventId || coupon.eventId;
      
      if (!attendeeId || !eventId) {
        message.error('필요한 정보가 부족합니다.');
        return;
      }
      
      // 당첨알림 발송 API 호출
      if (apiMethods && apiMethods.sendWinnerNotifications) {
        apiMethods.sendWinnerNotifications(
          eventId,
          {
            messageType: 'ALIM_TALK',
            attendeeIds: [attendeeId]
          },
          (response) => {
            // API 응답 구조 분석
            const responseData = response.data?.data || response.data;
            const results = responseData.results || [];
            const successCount = responseData.successCount || 0;
            const failedCount = responseData.failedCount || 0;
            
            // 결과 메시지 처리
            if (successCount > 0) {
              message.success('당첨알림이 재발송되었습니다.');
              // 부모 컴포넌트에 이벤트 발생시켜 데이터 새로고침
              emit('coupon-resent', coupon);
            } else if (failedCount > 0 && results.length > 0) {
              // 실패한 경우 실패 사유 표시
              const failReason = results[0].reason || '알 수 없는 오류';
              message.warning(`당첨알림 재발송 실패: ${failReason}`);
            } else {
              // 처리된 건이 없는 경우
              const summary = responseData.summary;
              message.warning(summary || '처리 가능한 상태가 아닙니다');
            }
          },
          (error) => {
            message.error('당첨알림 재발송 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
          }
        );
      } else {
        message.error('API 초기화 오류가 발생했습니다.');
      }
    };
    
    // 마이페이지 관련 메서드
    const viewMyPage = () => {
      // 마이페이지 탭으로 이동
      activeTab.value = 'mypage';
      // 데이터가 없을 때만 로드 (이미 드로어 열릴 때 로드했으므로)
      if (!myPageData.value) {
        loadMyPageData();
      }
    };
    
    const loadMyPageData = async () => {
      if (!props.participant.customerId) {
        myPageError.value = '고객 ID가 없습니다.';
        return;
      }
      
      // 이미 로딩 중이면 중복 호출 방지
      if (myPageLoading.value) {
        return;
      }
      
      myPageLoading.value = true;
      myPageError.value = null;
      
      try {
        // API 호출을 위한 axios 인스턴스 가져오기
        const apiInstance = instance?.proxy?.$apiInstance;
        if (!apiInstance) {
          throw new Error('API 인스턴스를 찾을 수 없습니다.');
        }
        
        // 관리자 토큰으로 고객 정보 조회 API 호출
        const response = await apiInstance.get('/customers/me', {
          params: {
            customerId: props.participant.customerId
          }
        });
        
        if (response.data && response.data.data) {
          myPageData.value = response.data.data;
        } else {
          myPageData.value = response.data;
        }
        
        console.log('마이페이지 데이터 로드 완료:', myPageData.value);
      } catch (error) {
        console.error('마이페이지 데이터 로드 실패:', error);
        myPageError.value = error.response?.data?.message || '고객 정보를 불러오는 중 오류가 발생했습니다.';
        // 에러 메시지는 마이페이지 탭을 열었을 때만 표시
        if (activeTab.value === 'mypage') {
          message.error(myPageError.value);
        }
      } finally {
        myPageLoading.value = false;
      }
    };
    
    const refreshMyPageData = () => {
      loadMyPageData();
    };

    // 드로워 전체 데이터 새로고침
    const refreshParticipantData = async () => {
      if (!props.participant.customerId) {
        message.warning('고객 ID가 없습니다.');
        return;
      }

      refreshLoading.value = true;

      try {
        // API 호출을 위한 axios 인스턴스 가져오기
        const apiInstance = instance?.proxy?.$apiInstance;
        if (!apiInstance) {
          throw new Error('API 인스턴스를 찾을 수 없습니다.');
        }

        // 부모 컴포넌트에 새로고침 요청 (이벤트 목록 + 참여자 목록)
        emit('refresh');

        // 고객 정보 조회 API 호출
        const response = await apiInstance.get('/customers/me', {
          params: {
            customerId: props.participant.customerId
          }
        });

        if (response.data && response.data.data) {
          myPageData.value = response.data.data;
        } else {
          myPageData.value = response.data;
        }

        message.success('데이터가 새로고침되었습니다.');
      } catch (error) {
        console.error('데이터 새로고침 실패:', error);
        message.error('데이터 새로고침에 실패했습니다.');
      } finally {
        refreshLoading.value = false;
      }
    };

    // 마이페이지 테이블 컬럼 정의
    const animalColumns = [
      { title: '이름', dataIndex: 'name', key: 'name' },
      { title: '품종', dataIndex: 'variety', key: 'variety' },
      { title: '성별', key: 'gender', width: '80px' },
      { title: '체중', key: 'weight', width: '80px' },
      { title: '생년월일', dataIndex: 'birthDate', key: 'birthDate' }
    ];
    
    const hospitalColumns = [
      { title: '병원명', dataIndex: 'name', key: 'name' },
      { title: '방문일', key: 'createAt' }
    ];
    
    const unissuedCouponColumns = [
      { title: '이벤트명', dataIndex: 'eventTitle', key: 'eventTitle' },
      { title: '참여일', key: 'participatedAt' },
      { title: '상태', key: 'status', width: '100px' }
    ];
    
    const issuedCouponColumns = [
      { title: '쿠폰명', dataIndex: 'couponName', key: 'couponName' },
      { title: '쿠폰번호', dataIndex: 'couponNumber', key: 'couponNumber' },
      { title: '발급일', key: 'issuedAt' },
      { title: '만료일', key: 'expiredAt' },
      { title: '상태', key: 'status', width: '100px' }
    ];
    
    const getEventStatusTagColor = (status) => {
      const colors = {
        'completed': 'green',
        'in_progress': 'blue',
        'participated': 'orange'
      };
      return colors[status] || 'default';
    };
    
    // 성별 텍스트 변환
    const getGenderText = (gender) => {
      const genderMap = {
        'M': '남성',
        'F': '여성',
        'MALE': '남성',
        'FEMALE': '여성'
      };
      return genderMap[gender] || gender || '-';
    };
    
    // 가입 경로 텍스트 변환
    const getOriginSourceText = (source) => {
      const sourceMap = {
        'CUSTOMER_WEB_REGISTER': '웹 가입',
        'APP_REGISTER': '앱 가입',
        'ADMIN_REGISTER': '관리자 등록'
      };
      return sourceMap[source] || source || '-';
    };
    
    // 동물 타입 텍스트 변환
    const getAnimalTypeText = (type) => {
      const typeMap = {
        'DOG': '강아지',
        'CAT': '고양이',
        'BIRD': '새',
        'FISH': '물고기',
        'HAMSTER': '햄스터',
        'RABBIT': '토끼'
      };
      return typeMap[type] || type || '-';
    };
    
    // 동물 아이콘 가져오기
    const getAnimalIcon = (type) => {
      const iconMap = {
        'DOG': 'fa fa-paw',
        'CAT': 'fa fa-paw',
        'BIRD': 'fa fa-twitter',
        'FISH': 'fa fa-fish',
        'HAMSTER': 'fa fa-paw',
        'RABBIT': 'fa fa-paw'
      };
      return iconMap[type] || 'fa fa-paw';
    };
    
    // 병원 방문 이력 데이터 포맷
    const formattedHospitalData = computed(() => {
      if (!myPageData.value?.companyMetadatas) return [];
      return myPageData.value.companyMetadatas.map((item, index) => ({
        key: index,
        ...item
      }));
    });
    
    // 전체 쿠폰 수 계산
    const totalCouponCount = computed(() => {
      if (!myPageData.value) return 0;
      const unissued = myPageData.value.unissuedCoupons?.length || 0;
      const issued = myPageData.value.issuedCoupons?.length || 0;
      return unissued + issued;
    });
    
    const handleDeleteCoupon = async (coupon) => {
      // 쿠폰 상태에 따른 메시지 분기
      let dialogTitle = '쿠폰 삭제';
      let dialogMessage = '';
      
      if (coupon.status === 'winner_selected') {
        // 수령 대기 상태인 경우
        dialogMessage = `쿠폰 발급 권한을 제거하시겠습니까?`;
      } else {
        // 그 외 상태인 경우
        dialogMessage = `쿠폰 번호 "${coupon.couponNumber || '-'}"를 삭제하시겠습니까?\n삭제된 쿠폰은 더 이상 사용할 수 없습니다.`;
      }
      
      // 삭제 확인 다이얼로그
      const isConfirmed = await twoBtnDialog(
        dialogTitle,
        dialogMessage,
        '삭제'
      );
      
      if (!isConfirmed) return;
      
      // API 호출
      if (apiMethods && apiMethods.removeCoupons) {
        const attendeeId = props.participant._id || props.participant.id;
        const eventId = props.participant.eventId || coupon.eventId;
        
        if (!attendeeId || !eventId || !coupon.couponNumber) {
          message.error('필요한 정보가 부족합니다.');
          return;
        }
        
        apiMethods.removeCoupons(
          eventId,
          [attendeeId],
          coupon.couponNumber,
          (response) => {
            message.success('쿠폰이 삭제되었습니다.');
            // 부모 컴포넌트에 이벤트 발생시켜 데이터 새로고침
            emit('coupon-deleted', coupon);
          },
          (error) => {
            message.error('쿠폰 삭제 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
          }
        );
      } else {
        message.error('API 초기화 오류가 발생했습니다.');
      }
    };
    
    const handleRevokeCoupon = async (coupon) => {
      // 회수 확인 다이얼로그
      const isConfirmed = await twoBtnDialog(
        '쿠폰 회수',
        `쿠폰 번호 "${coupon.couponNumber || '-'}"을(를) 회수하시겠습니까?\n회수된 쿠폰은 사용할 수 없지만 데이터는 보존됩니다.`,
        '회수'
      );

      if (!isConfirmed) return;

      // API 호출
      if (apiMethods && apiMethods.revokeCoupon) {
        const attendeeId = props.participant._id || props.participant.id;
        const eventId = props.participant.eventId || coupon.eventId;

        if (!attendeeId || !eventId || !coupon.couponNumber) {
          message.error('필요한 정보가 부족합니다.');
          return;
        }

        apiMethods.revokeCoupon(
          eventId,
          attendeeId,
          coupon.couponNumber,
          '관리자 회수',
          (response) => {
            message.success('쿠폰이 회수되었습니다.');
            // 부모 컴포넌트에 이벤트 발생시켜 데이터 새로고침
            emit('coupon-deleted', coupon);
          },
          (error) => {
            message.error('쿠폰 회수 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
          }
        );
      } else {
        message.error('API 초기화 오류가 발생했습니다.');
      }
    };

    // 쿠폰 사용상태 조회 (SendB API) - 테이블에서 개별 쿠폰 조회
    const checkCouponUsageStatus = async (coupon) => {
      // couponTransactionId 사용 (SendB txId)
      const transactionId = coupon.couponTransactionId;

      if (!transactionId) {
        message.warning('쿠폰 트랜잭션 ID가 없어 조회할 수 없습니다.');
        return;
      }

      // 이미 조회 중이면 중복 요청 방지
      if (couponStatusLoading.value[transactionId]) {
        return;
      }

      couponStatusLoading.value[transactionId] = true;

      try {
        const apiInstance = instance?.proxy?.$apiInstance;
        if (!apiInstance) {
          message.error('API 초기화 오류가 발생했습니다.');
          couponStatusLoading.value[transactionId] = false;
          return;
        }

        // couponTransactionId로 조회
        const response = await apiInstance.get(`/sendb/coupon-status/transaction/${transactionId}`);
        const data = response.data?.data;

        if (data?.couponList && data.couponList.length > 0) {
          const couponInfo = data.couponList[0];
          couponUsageStatus.value[transactionId] = {
            status: couponInfo.cpnStatus,
            expiryDate: couponInfo.expiryDate,
            receiveNum: couponInfo.receiveNum,
            data: couponInfo,
            checkedAt: new Date().toISOString()
          };

          const statusText = getSendbCouponStatusText(couponInfo.cpnStatus);
          message.success(`쿠폰 상태: ${statusText}`);
        } else {
          couponUsageStatus.value[transactionId] = {
            status: 'NOT_FOUND',
            checkedAt: new Date().toISOString()
          };
          message.warning('쿠폰 정보를 찾을 수 없습니다.');
        }
      } catch (error) {
        console.error('쿠폰 상태 조회 실패:', error);
        couponUsageStatus.value[transactionId] = {
          status: 'ERROR',
          error: error.response?.data?.message || '조회 실패',
          checkedAt: new Date().toISOString()
        };
        message.error('쿠폰 상태 조회 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
      } finally {
        couponStatusLoading.value[transactionId] = false;
      }
    };

    // SendB 쿠폰 상태 텍스트 변환
    const getSendbCouponStatusText = (status) => {
      const statusMap = {
        '0': '전송전',
        'B': '사용가능',
        'C': '교환됨(사용완료)',
        'J': '기간만료',
        'H': '취소됨',
        'F': '발송실패',
        'NOT_FOUND': '조회불가',
        'ERROR': '조회오류'
      };
      return statusMap[status] || status;
    };

    // SendB 쿠폰 상태 색상 변환
    const getSendbCouponStatusColor = (status) => {
      const colorMap = {
        '0': 'default',
        'B': 'green',
        'C': 'purple',
        'J': 'orange',
        'H': 'red',
        'F': 'red',
        'NOT_FOUND': 'default',
        'ERROR': 'red'
      };
      return colorMap[status] || 'default';
    };

    // 쿠폰 추가 모달 열기
    const showAddCouponModal = () => {
      addCouponForm.value = {
        selectedProductId: null,
        couponNumber: '',
        reason: '',
        lookupType: 'txId',
        lookupValue: '',
        expiryDate: null
      };
      addCouponModalVisible.value = true;
    };

    // 쿠폰 추가 모달 닫기
    const closeAddCouponModal = () => {
      addCouponModalVisible.value = false;
      fetchedCouponStatus.value = null;
      addCouponForm.value = {
        selectedProductId: null,
        couponNumber: '',
        reason: '',
        lookupType: 'txId',
        lookupValue: '',
        expiryDate: null
      };
    };

    // 유효기간 조회 (SendB API)
    const fetchExpiryDate = async () => {
      const lookupValue = addCouponForm.value.lookupValue?.trim();
      const lookupType = addCouponForm.value.lookupType;

      if (!lookupValue) {
        message.warning(`${lookupType}를 입력해주세요.`);
        return;
      }

      if (!apiMethods || !apiMethods.getCouponStatus) {
        message.error('API 초기화 오류가 발생했습니다.');
        return;
      }

      expiryDateLoading.value = true;

      // 사용자가 선택한 타입에 따라 API 파라미터 결정
      const params = lookupType === 'txId'
        ? { transactionId: lookupValue }
        : { orderId: lookupValue };

      apiMethods.getCouponStatus(
        params,
        (response) => {
          expiryDateLoading.value = false;
          const data = response.data?.data;

          if (data?.couponList && data.couponList.length > 0) {
            const coupon = data.couponList[0];
            // 쿠폰 상태 저장 (화면 표시용)
            fetchedCouponStatus.value = coupon.cpnStatus;
            if (coupon.expiryDate) {
              // YYYY-MM-DD 형식의 문자열을 dayjs 객체로 변환
              addCouponForm.value.expiryDate = dayjs(coupon.expiryDate);
              message.success(`유효기간: ${coupon.expiryDate}`);
            } else {
              message.warning('유효기간 정보가 없습니다.');
            }
          } else {
            fetchedCouponStatus.value = null;
            message.warning('쿠폰 정보를 찾을 수 없습니다.');
          }
        },
        (error) => {
          expiryDateLoading.value = false;
          message.error('유효기간 조회 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
        }
      );
    };

    // 쿠폰 추가 처리
    const handleAddCoupon = async () => {
      const selectedProductId = addCouponForm.value.selectedProductId;
      const couponNumber = addCouponForm.value.couponNumber?.trim();

      if (!selectedProductId) {
        message.warning('상품을 선택해주세요.');
        return;
      }

      if (!couponNumber) {
        message.warning('쿠폰번호를 입력해주세요.');
        return;
      }

      // 선택된 상품 정보 찾기
      const selectedProduct = couponProductOptions.value.find(
        (p) => p.goodsId === selectedProductId
      );

      if (!selectedProduct) {
        message.error('선택된 상품 정보를 찾을 수 없습니다.');
        return;
      }

      // API 호출
      if (apiMethods && apiMethods.addCoupon) {
        const attendeeId = props.participant._id || props.participant.id;
        const eventId = props.participant.eventId;

        if (!attendeeId || !eventId) {
          message.error('필요한 정보가 부족합니다.');
          return;
        }

        addCouponLoading.value = true;

        // 유효기간 포맷팅 (dayjs 객체를 YYYY-MM-DD 문자열로 변환)
        const expiryDate = addCouponForm.value.expiryDate
          ? addCouponForm.value.expiryDate.format('YYYY-MM-DD')
          : null;

        // lookupType에 따라 txId 또는 orderId 결정
        const lookupType = addCouponForm.value.lookupType;
        const lookupValue = addCouponForm.value.lookupValue?.trim() || null;
        const txId = lookupType === 'txId' ? lookupValue : null;
        const orderId = lookupType === 'orderId' ? lookupValue : null;

        apiMethods.addCoupon(
          eventId,
          attendeeId,
          couponNumber,
          addCouponForm.value.reason || '관리자 수동 추가',
          selectedProduct,  // goodsInfo 전달
          expiryDate,       // expiryDate 전달
          txId,             // txId 전달
          orderId,          // orderId 전달
          (response) => {
            addCouponLoading.value = false;
            message.success(`쿠폰 ${couponNumber}이(가) 추가되었습니다.`);
            closeAddCouponModal();
            // 부모 컴포넌트에 이벤트 발생시켜 데이터 새로고침
            emit('coupon-deleted', { couponNumber });  // 기존 이벤트 재활용 (데이터 새로고침용)
          },
          (error) => {
            addCouponLoading.value = false;
            message.error('쿠폰 추가 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
          }
        );
      } else {
        message.error('API 초기화 오류가 발생했습니다.');
      }
    };

    // 원본 데이터 복사
    const copyRawData = () => {
      const jsonStr = JSON.stringify(props.participant, null, 2);
      navigator.clipboard.writeText(jsonStr).then(() => {
        message.success('원본 데이터가 클립보드에 복사되었습니다.');
      }).catch(() => {
        message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
      });
    };
    
    // JSON 클릭 처리 (접기/펼치기)
    const handleJsonClick = (event) => {
      const target = event.target;
      // json-toggle 클래스를 가진 요소 클릭 시 처리
      if (target.classList.contains('json-toggle')) {
        const path = target.getAttribute('data-path');
        if (path) {
          if (collapsedPaths.value.has(path)) {
            collapsedPaths.value.delete(path);
          } else {
            collapsedPaths.value.add(path);
          }
        }
      }
    };
    
    // 모두 접기/펼치기
    const toggleAllJson = () => {
      if (jsonExpanded.value) {
        // 모두 접기 - 모든 경로를 collapsed에 추가
        const allPaths = document.querySelectorAll('.json-toggle');
        allPaths.forEach(el => {
          const path = el.getAttribute('data-path');
          if (path) collapsedPaths.value.add(path);
        });
      } else {
        // 모두 펼치기 - collapsed 초기화
        collapsedPaths.value.clear();
      }
      jsonExpanded.value = !jsonExpanded.value;
    };

    // 활동 상세보기 모달 관련 메서드
    const showActivityDetail = (activity) => {
      selectedActivity.value = activity;
      activityDetailVisible.value = true;
    };

    const closeActivityDetail = () => {
      activityDetailVisible.value = false;
      selectedActivity.value = null;
    };

    const formatDetailKey = (key) => {
      // camelCase를 읽기 쉬운 형태로 변환
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    };

    const formatDetailValue = (value) => {
      if (value === null || value === undefined) return '-';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };

    const copyActivityData = () => {
      if (!selectedActivity.value) return;
      const jsonStr = JSON.stringify(selectedActivity.value, null, 2);
      navigator.clipboard.writeText(jsonStr).then(() => {
        message.success('활동 데이터가 클립보드에 복사되었습니다.');
      }).catch(() => {
        message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
      });
    };
    
    return {
      drawerWidth,
      isMobileView,
      activeTab,
      timelineSort,
      messageSort,
      metrics,
      getCurrentStep,
      getStepPercent,
      friendParticipationCount,
      couponUsedCount,
      couponTotalCount,
      activityScore,
      formattedJsonHtml,
      jsonExpanded,
      couponColumns,
      couponDataSource,
      displayActivities,
      displayMessages,
      messageStats,
      handleClose,
      getStatusColor,
      getStatusText,
      formatRelativeTime,
      formatDate,
      formatDateTime,
      // SNS 연동 조회 관련
      snsSearchCustomerId,
      snsSearchLoading,
      snsSearchResult,
      snsSearchedCustomerId,
      snsTableColumns,
      getSnsTypeColor,
      fillCurrentCustomerId,
      searchCustomerSns,
      // 기타
      getAvatarColor,
      getCouponStatusType,
      getCouponStatusText,
      getCouponStatusColor,
      getActivityColor,
      getActivityIcon,
      getMessageStatusColor,
      getMessageStatusText,
      getMessageStatusIcon,
      handleSendCoupon,
      handleResendCoupon,
      handleDeleteCoupon,
      handleRevokeCoupon,
      // 쿠폰 사용상태 조회 관련
      couponUsageStatus,
      couponStatusLoading,
      checkCouponUsageStatus,
      getSendbCouponStatusText,
      getSendbCouponStatusColor,
      // 쿠폰 추가 모달 관련
      addCouponModalVisible,
      addCouponLoading,
      expiryDateLoading,
      fetchedCouponStatus,
      addCouponForm,
      couponProductOptions,
      showAddCouponModal,
      closeAddCouponModal,
      handleAddCoupon,
      getCouponStatusClass,
      getCouponStatusIcon,
      fetchExpiryDate,
      copyRawData,
      handleJsonClick,
      toggleAllJson,
      // 마이페이지 관련
      myPageLoading,
      myPageData,
      myPageError,
      viewMyPage,
      loadMyPageData,
      refreshMyPageData,
      refreshParticipantData,
      refreshLoading,
      animalColumns,
      hospitalColumns,
      unissuedCouponColumns,
      issuedCouponColumns,
      getEventStatusTagColor,
      getGenderText,
      getOriginSourceText,
      getAnimalTypeText,
      getAnimalIcon,
      formattedHospitalData,
      totalCouponCount,
      // 활동 상세보기 모달 관련
      activityDetailVisible,
      selectedActivity,
      showActivityDetail,
      closeActivityDetail,
      formatDetailKey,
      formatDetailValue,
      copyActivityData,
      // eventmetrics 관련
      eventMetrics,
      eventMetricsLoading,
      eventMetricsError,
      loadEventMetrics,
      // 헤더 접기/펼치기
      isHeaderCollapsed,
      toggleHeader,
      // 참여 이력 관련
      eventId,
      attendeeHistory,
      attendeeHistoryLoading,
      attendeeHistoryError,
      getHistoryStatusColor,
      getHistoryStatusText,
      // 추천 관계
      referralsData,
      referralsLoading,
      referralsError
    };
  }
};
</script>

<style scoped>
.participant-detail-drawer :deep(.ant-drawer-body) {
  padding: 0 !important;
  overflow: hidden !important;
  height: 100%;
}

.drawer-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.detail-header {
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  flex-shrink: 0;
}

.participant-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.main-info {
  display: flex;
  gap: 16px;
  align-items: center;
}

.participant-avatar {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.info-text h2 {
  margin: 0;
  color: white;
  font-size: 20px;
  font-weight: 600;
}

.id-info {
  margin-top: 4px;
  font-size: 13px;
  opacity: 0.85;
}

.participant-id,
.customer-id {
  font-weight: 400;
}

.sub-info {
  margin-top: 8px;
  opacity: 0.95;
  font-size: 14px;
}

.sub-info i {
  margin-right: 4px;
}

.status-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.refresh-btn-header {
  margin-bottom: 4px;
}

.refresh-btn-header i {
  margin-right: 4px;
}

.last-activity {
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
}


.detail-tabs {
  flex: 1;
  overflow: hidden;
}

.detail-tabs :deep(.ant-tabs) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.detail-tabs :deep(.ant-tabs-nav) {
  padding: 0 24px;
  margin-bottom: 0;
  flex-shrink: 0;
  background: white;
}

.detail-tabs :deep(.ant-tabs-content-holder) {
  flex: 1;
  overflow: hidden;
}

.detail-tabs :deep(.ant-tabs-content) {
  height: 100%;
}

.detail-tabs :deep(.ant-tabs-tabpane) {
  height: 100%;
  overflow-y: auto;
  padding: 24px;
}

.detail-tabs :deep(.ant-tabs-tabpane-active) {
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #262626;
}

.section-title-with-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title-with-action .section-title {
  margin-bottom: 0;
}


/* 쿠폰 정보 스타일 */
.coupon-container {
  padding: 16px 0;
}

/* 쿠폰 사용상태 셀 스타일 */
.usage-status-cell {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

/* 타임라인 스타일 */
.timeline-container {
  padding: 16px 0;
}

.timeline-sort-toggle {
  float: right;
}

.timeline-wrapper {
  position: relative;
}

.timeline-wrapper :deep(.ant-timeline) {
  padding-top: 20px;
}

.timeline-wrapper :deep(.ant-timeline-item) {
  position: relative;
  padding-bottom: 40px;
}

.timeline-wrapper :deep(.ant-timeline-item:last-child) {
  padding-bottom: 20px;
}

.timeline-wrapper :deep(.ant-timeline-item-tail) {
  border-left-style: dashed;
  border-left-width: 2px;
}

.timeline-dot-wrapper {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 50%;
}

.timeline-start-top,
.timeline-start-bottom,
.timeline-end-top,
.timeline-end-bottom {
  padding-left: 40px;
  color: #8c8c8c;
  font-size: 14px;
}

.timeline-start-top,
.timeline-end-top {
  margin-bottom: 10px;
}

.timeline-start-bottom,
.timeline-end-bottom {
  margin-top: 10px;
}

.timeline-start-top i,
.timeline-start-bottom i {
  margin-right: 6px;
  color: #52c41a;
}

.timeline-end-top i,
.timeline-end-bottom i {
  margin-right: 6px;
  color: #ff4d4f;
}

.timeline-content {
  padding: 8px 0;
  margin-left: 8px;
}

.activity-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.activity-title-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.activity-type {
  font-weight: 600;
  color: #262626;
  font-size: 14px;
}

.activity-btn-name {
  font-weight: 400;
  color: #1890ff;
  font-size: 12px;
  margin-left: 2px;
}

.activity-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.activity-time {
  color: #8c8c8c;
  font-size: 12px;
  white-space: nowrap;
}

.activity-detail-btn {
  padding: 0 8px;
  height: 24px;
  font-size: 12px;
  line-height: 22px;
}

.activity-detail-btn i {
  margin-right: 4px;
}

.activity-details {
  margin: 8px 0;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.activity-detail {
  color: #595959;
  font-size: 14px;
  margin-bottom: 8px;
}

.activity-metadata {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* 스크롤바 스타일 */
.timeline-container::-webkit-scrollbar {
  width: 6px;
}

.timeline-container::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.timeline-container::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 3px;
}

.timeline-container::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

/* 버튼 스타일 */
.yellow-button {
  background-color: #FFD814 !important;
  border-color: #FFD814 !important;
}

.yellow-button:hover {
  background-color: #E5C112 !important;
  border-color: #E5C112 !important;
}

/* 원본 데이터 스타일 */
.rawdata-container {
  padding: 16px 0;
  height: calc(100vh - 350px);
  display: flex;
  flex-direction: column;
}

.json-viewer {
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 16px;
  overflow: auto;
  flex: 1;
}

.json-viewer pre {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
}

/* JSON 색상 스타일 */
.json-key {
  color: #881391;
  font-weight: 500;
}

.json-string {
  color: #0a3069;
}

.json-number {
  color: #0550ae;
}

.json-boolean {
  color: #cf222e;
  font-weight: 600;
}

.json-null {
  color: #6e7781;
  font-weight: 600;
}

.json-bracket {
  color: #24292f;
  font-weight: 600;
}

/* 접기/펼치기 스타일 */
.json-toggle {
  cursor: pointer;
  user-select: none;
  display: inline-block;
  width: 16px;
  color: #6e7781;
  font-size: 11px;
  font-family: monospace;
  text-align: center;
  margin-right: 2px;
}

.json-toggle:hover {
  background-color: rgba(139, 148, 158, 0.1);
  border-radius: 2px;
}

.json-ellipsis {
  color: #6e7781;
  font-style: italic;
  font-weight: normal;
  padding: 0 4px;
}

/* 스크롤바 스타일 */
.json-viewer::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.json-viewer::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.json-viewer::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 4px;
}

.json-viewer::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

/* 메시지 이력 스타일 */
.message-container {
  padding: 16px 0;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

.message-info {
  margin-top: 12px;
}

.message-template {
  color: #595959;
  font-size: 13px;
  margin-bottom: 8px;
}

.message-content {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  margin: 8px 0;
}

.message-text {
  color: #262626;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
}

.message-interaction {
  margin-top: 8px;
}

.message-error {
  margin-top: 12px;
}

/* 메시지 스크롤바 스타일 */
.message-container::-webkit-scrollbar {
  width: 6px;
}

.message-container::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.message-container::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 3px;
}

.message-container::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

/* 리뷰 정보 스타일 */
.review-container {
  padding: 16px 0;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
}

.reviews-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.review-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: box-shadow 0.3s;
}

.review-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
}

.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.review-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.review-score {
  font-weight: 600;
  color: #262626;
  margin-left: 8px;
}

.review-date {
  color: #8c8c8c;
  font-size: 13px;
}

.review-content {
  margin: 16px 0;
  padding: 12px;
  background: #fafafa;
  border-radius: 4px;
}

.review-content p {
  margin: 0;
  color: #262626;
  line-height: 1.6;
}

.review-content.no-content p {
  color: #8c8c8c;
  font-style: italic;
}

.satisfaction-points {
  margin: 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.points-label {
  font-weight: 500;
  color: #595959;
  margin-right: 4px;
}

.review-meta {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  font-size: 13px;
  color: #8c8c8c;
}

.review-meta i {
  margin-right: 4px;
}

/* 리뷰 스크롤바 스타일 */
.review-container::-webkit-scrollbar {
  width: 6px;
}

.review-container::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.review-container::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 3px;
}

.review-container::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

.yellow-button {
  background-color: #FFD814;
  border-color: #FFD814;
}

.yellow-button:hover {
  background-color: #E5C112;
  border-color: #E5C112;
}

.ml-2 {
  margin-left: 8px;
}

/* 마이페이지 스타일 */
.mypage-container {
  padding: 0;
}

/* 마이페이지 헤더 - 스크린샷 스타일 참고 */
.mypage-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  padding: 24px;
  margin-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.profile-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  position: relative;
}

.profile-avatar {
  background-color: #e8e8e8;
  color: #999;
}

.profile-info h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #262626;
}

.profile-info p {
  margin: 4px 0 0;
  font-size: 14px;
  color: #8c8c8c;
}

.refresh-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

/* 빠른 메뉴 스타일 - 스크린샷 참고 */
.quick-menu {
  display: flex;
  gap: 24px;
  justify-content: space-around;
  padding: 20px;
  background: #fafafa;
  border-radius: 12px;
}

.menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;
  padding: 8px;
  border-radius: 8px;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
}

.menu-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  position: relative;
}

.coupon-icon {
  background: linear-gradient(135deg, #c3b5ff 0%, #9c88ff 100%);
  color: white;
}

.booking-icon {
  background: linear-gradient(135deg, #a8d8ff 0%, #74b9ff 100%);
  color: white;
}

.cert-icon {
  background: linear-gradient(135deg, #ffd93d 0%, #ffb81c 100%);
  color: white;
}

.menu-icon .badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #ff4757;
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.menu-item span {
  font-size: 13px;
  color: #595959;
  font-weight: 500;
}

/* 정보 카드 스타일 */
.info-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 16px;
}

.info-card :deep(.ant-card-head) {
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.info-card :deep(.ant-card-head-title) {
  font-size: 15px;
  font-weight: 600;
}

/* 참여 이력 카드 스타일 */
.attendee-history-card .card-title-with-badge {
  display: flex;
  align-items: center;
  gap: 8px;
}

.attendee-history-card .history-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.attendee-history-card .history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attendee-history-card .history-item {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px 16px;
  transition: all 0.2s;
}

.attendee-history-card .history-item:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.attendee-history-card .history-item.current-event {
  background: #e6f7ff;
  border-color: #91d5ff;
}

.attendee-history-card .history-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 4px;
}

.attendee-history-card .event-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.attendee-history-card .event-title {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
}

.attendee-history-card .current-tag {
  font-size: 11px;
}

.attendee-history-card .registration-date {
  font-size: 12px;
  color: #8c8c8c;
}

.attendee-history-card .history-item-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.attendee-history-card .info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.attendee-history-card .info-row .label {
  color: #8c8c8c;
  min-width: 60px;
}

.attendee-history-card .info-row .value {
  color: #262626;
}

.attendee-history-card .info-row .coupon-number {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.attendee-history-card .coupon-info-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e8e8e8;
}

.attendee-history-card .coupon-item {
  background: white;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 6px;
}

.attendee-history-card .coupon-item:last-child {
  margin-bottom: 0;
}

.attendee-history-card .no-coupon-info {
  font-size: 12px;
  padding: 4px 0;
}

.mypage-container .ant-descriptions-item-label {
  background-color: #fafafa;
  font-weight: 500;
  color: #595959;
}

.mypage-container .ant-descriptions-item-content {
  color: #262626;
}

/* 반려동물 섹션 */
.animal-section {
  margin-bottom: 16px;
}

.animal-section h5 {
  font-size: 14px;
  font-weight: 600;
  color: #595959;
  margin-bottom: 12px;
}

.animal-section h5 i {
  margin-right: 6px;
  color: #8c8c8c;
}

/* 테이블 스타일 */
.mypage-container .ant-table-wrapper {
  margin-top: 8px;
}

.mypage-container .ant-table-thead > tr > th {
  background: #fafafa;
  font-weight: 600;
  font-size: 13px;
}

.mypage-container .ant-table-tbody > tr > td {
  font-size: 13px;
}

.mb-3 {
  margin-bottom: 16px;
}

.mt-2 {
  margin-top: 8px;
}

.json-viewer-simple {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
  line-height: 1.4;
}

/* 활동 상세보기 모달 스타일 */
.activity-detail-modal {
  max-height: calc(70vh + 50px);
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 24px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.detail-section-title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.detail-section-title i {
  margin-right: 8px;
  color: #1890ff;
}

.json-viewer-modal {
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px;
  max-height: 300px;
  overflow: auto;
  margin-bottom: 12px;
}

.json-viewer-modal pre {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
}

.copy-btn {
  float: right;
}

.copy-btn i {
  margin-right: 4px;
}

.activity-detail-modal .ant-descriptions-item-label {
  background-color: #fafafa;
  font-weight: 500;
  color: #595959;
  min-width: 140px;
}

.activity-detail-modal .ant-descriptions-item-content {
  color: #262626;
  word-break: break-word;
}

/* 모달 스크롤바 스타일 */
.activity-detail-modal::-webkit-scrollbar {
  width: 6px;
}

.activity-detail-modal::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.activity-detail-modal::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 3px;
}

.activity-detail-modal::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

.json-viewer-modal::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.json-viewer-modal::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.json-viewer-modal::-webkit-scrollbar-thumb {
  background: #bfbfbf;
  border-radius: 3px;
}

.json-viewer-modal::-webkit-scrollbar-thumb:hover {
  background: #8c8c8c;
}

/* 헤더 접기/펼치기 스타일 */
.header-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
  transition: all 0.2s ease;
}

.header-summary:hover {
  opacity: 0.9;
}

.summary-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.participant-avatar-small {
  background: rgba(255, 255, 255, 0.3) !important;
}

.summary-name {
  font-size: 16px;
  font-weight: 600;
}

.summary-right {
  display: flex;
  align-items: center;
}

.collapse-toggle-btn {
  color: white !important;
  background: rgba(255, 255, 255, 0.2) !important;
  border-radius: 16px !important;
  font-size: 12px !important;
  padding: 4px 12px !important;
  height: auto !important;
}

.collapse-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.3) !important;
}

.collapse-toggle-btn i {
  margin-right: 4px;
}

.header-detail {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.header-collapsed {
  padding: 16px 24px !important;
}

.header-collapsed .header-summary {
  padding: 0;
}

/* 모바일 쿠폰 카드 스타일 */
.coupon-card-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.coupon-card {
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

.coupon-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.coupon-card.coupon-deleted {
  background: #fafafa;
  opacity: 0.7;
}

.coupon-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #f0f0f0;
}

.coupon-name {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  word-break: keep-all;
}

.coupon-name i {
  color: #9c88ff;
  font-size: 18px;
  flex-shrink: 0;
}

.coupon-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.coupon-card-body {
  margin-bottom: 12px;
}

.coupon-info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 13px;
}

.coupon-info-row:not(:last-child) {
  border-bottom: 1px solid #f5f5f5;
}

.coupon-info-row .label {
  color: #8c8c8c;
  font-weight: 500;
}

.coupon-info-row .value {
  color: #262626;
  font-weight: 500;
}

/* 추가사유 텍스트 스타일 */
.add-reason-text {
  color: #595959;
  cursor: pointer;
  border-bottom: 1px dashed #d9d9d9;
}

.add-reason-text:hover {
  color: #1890ff;
  border-bottom-color: #1890ff;
}

.text-muted {
  color: #bfbfbf;
}

.coupon-card-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.coupon-card-footer .ant-btn {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 쿠폰 사용상태 셀 스타일 */
.usage-status-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.usage-status-cell .refresh-btn {
  padding: 0 4px;
  min-width: auto;
  height: 22px;
  line-height: 22px;
  color: #666;
}

.usage-status-cell .refresh-btn:hover {
  color: #1890ff;
}

.usage-status-cell .refresh-btn .fa-refresh {
  font-size: 12px;
}

/* 모바일 반응형 스타일 */
@media (max-width: 767px) {
  .participant-detail-drawer :deep(.ant-drawer-content-wrapper) {
    max-width: 100vw !important;
  }

  .participant-detail-drawer :deep(.ant-drawer-header) {
    padding: 12px 16px;
  }

  .participant-detail-drawer :deep(.ant-drawer-body) {
    padding: 12px;
  }

  .detail-header {
    padding: 12px !important;
  }

  .participant-info {
    flex-direction: column !important;
    gap: 12px !important;
  }

  .main-info {
    flex-direction: column !important;
    text-align: center;
  }

  .info-text h2 {
    font-size: 16px !important;
  }

  .sub-info {
    flex-wrap: wrap;
    justify-content: center;
    font-size: 12px !important;
  }

  .sub-info .ant-divider {
    display: none;
  }

  .sub-info span {
    display: block;
    width: 100%;
    text-align: center;
    margin: 4px 0;
  }

  .status-info {
    justify-content: center !important;
    flex-wrap: wrap;
    gap: 8px !important;
  }

  .quick-menu {
    flex-wrap: wrap;
    gap: 8px !important;
  }

  .menu-item {
    flex: 1 1 30% !important;
    min-width: 80px;
  }

  .detail-tabs :deep(.ant-tabs-nav) {
    padding: 0 8px;
  }

  .detail-tabs :deep(.ant-tabs-tab) {
    padding: 8px 12px !important;
    font-size: 13px !important;
  }

  /* 마이페이지 모바일 스타일 */
  .mypage-header {
    padding: 16px !important;
  }

  .profile-section {
    flex-direction: column;
    text-align: center;
    gap: 12px !important;
  }

  .profile-info {
    text-align: center;
  }

  .profile-info h3 {
    font-size: 18px !important;
  }

  .refresh-btn {
    position: static !important;
    transform: none !important;
    margin-top: 8px;
  }

  .quick-menu {
    padding: 12px !important;
  }

  .menu-icon {
    width: 48px !important;
    height: 48px !important;
    font-size: 20px !important;
  }

  .menu-item span {
    font-size: 12px !important;
  }

  /* 마이페이지 정보 카드 모바일 스타일 */
  .info-card {
    margin-bottom: 12px !important;
  }

  .info-card :deep(.ant-card-head) {
    padding: 12px 16px !important;
  }

  .info-card :deep(.ant-card-head-title) {
    font-size: 14px !important;
  }

  .info-card :deep(.ant-card-body) {
    padding: 12px !important;
  }

  /* a-descriptions 모바일 스타일 */
  .info-card :deep(.ant-descriptions-item-label) {
    padding: 8px 12px !important;
    font-size: 12px !important;
    min-width: 80px !important;
    width: 80px !important;
  }

  .info-card :deep(.ant-descriptions-item-content) {
    padding: 8px 12px !important;
    font-size: 13px !important;
    word-break: break-all;
  }

  /* 테이블 모바일 스타일 */
  .info-card :deep(.ant-table-wrapper) {
    overflow-x: auto;
  }

  .info-card :deep(.ant-table) {
    font-size: 12px !important;
  }

  .info-card :deep(.ant-table-thead > tr > th) {
    padding: 8px !important;
    font-size: 12px !important;
    white-space: nowrap;
  }

  .info-card :deep(.ant-table-tbody > tr > td) {
    padding: 8px !important;
    font-size: 12px !important;
  }

  /* 반려동물 섹션 모바일 */
  .animal-section h5 {
    font-size: 13px !important;
  }
}

/* 태블릿 반응형 스타일 */
@media (min-width: 768px) and (max-width: 1023px) {
  .participant-detail-drawer :deep(.ant-drawer-body) {
    padding: 16px;
  }

  .detail-header {
    padding: 16px !important;
  }

  .sub-info {
    font-size: 13px !important;
  }
}

/* 쿠폰 추가 모달 스타일 */
.add-coupon-modal-content {
  padding: 8px 24px 24px 24px;

  .modal-description {
    color: #666;
    margin-bottom: 16px;
    font-size: 14px;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }

  .expiry-date-row {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .expiry-date-hint {
    margin-top: 4px;
    font-size: 12px;
    color: #999;
  }

  .coupon-status-display {
    margin-top: 8px;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .coupon-status-display i {
    font-size: 16px;
  }

  .coupon-status-display.status-available {
    background-color: #f6ffed;
    border: 1px solid #b7eb8f;
    color: #52c41a;
  }

  .coupon-status-display.status-used {
    background-color: #e6f7ff;
    border: 1px solid #91d5ff;
    color: #1890ff;
  }

  .coupon-status-display.status-expired {
    background-color: #fff1f0;
    border: 1px solid #ffa39e;
    color: #f5222d;
  }

  .coupon-status-display.status-cancelled {
    background-color: #fff7e6;
    border: 1px solid #ffd591;
    color: #fa8c16;
  }

  .coupon-status-display.status-pending {
    background-color: #f9f9f9;
    border: 1px solid #d9d9d9;
    color: #8c8c8c;
  }

  .coupon-status-display.status-failed {
    background-color: #fff2e8;
    border: 1px solid #ffbb96;
    color: #fa541c;
  }
}

/* 참여 이력 탭 스타일 */
.history-tab-container {
  padding: 16px;
}

.history-tab-container .section-title-with-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.history-tab-container .section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.history-tab-container .history-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.history-tab-container .history-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.history-tab-container .history-item {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px 16px;
  transition: all 0.2s;
}

.history-tab-container .history-item:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.history-tab-container .history-item.current-event {
  background: #e6f7ff;
  border-color: #91d5ff;
}

.history-tab-container .history-item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 4px;
}

.history-tab-container .event-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.history-tab-container .event-title {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
}

.history-tab-container .current-tag {
  font-size: 11px;
}

.history-tab-container .registration-date {
  font-size: 12px;
  color: #8c8c8c;
}

.history-tab-container .history-item-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.history-tab-container .info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.history-tab-container .info-row .label {
  color: #8c8c8c;
  min-width: 60px;
}

.history-tab-container .info-row .value {
  color: #262626;
}

.history-tab-container .info-row .coupon-number {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}

.history-tab-container .coupon-info-section {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #e8e8e8;
}

.history-tab-container .coupon-item {
  background: white;
  border-radius: 6px;
  padding: 8px;
  margin-bottom: 6px;
}

.history-tab-container .coupon-item:last-child {
  margin-bottom: 0;
}

.history-tab-container .no-coupon-info {
  font-size: 13px;
}

/* 추천 관계 탭 스타일 */
.referrals-tab-container {
  padding: 16px;
}

.referrals-tab-container .referrals-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.referrals-tab-container .referrals-section {
  margin-bottom: 24px;
}

.referrals-tab-container .section-title {
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.referrals-tab-container .section-title i {
  margin-right: 8px;
  color: #1890ff;
}

.referrals-tab-container .section-title-with-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.referrals-tab-container .referrer-card {
  background: #f6ffed;
  border-color: #b7eb8f;
}

.referrals-tab-container .referrer-info .info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 8px;
}

.referrals-tab-container .referrer-info .info-row:last-child {
  margin-bottom: 0;
}

.referrals-tab-container .referrer-info .info-row .label {
  color: #8c8c8c;
  min-width: 70px;
}

.referrals-tab-container .referrer-info .info-row .value {
  color: #262626;
  font-weight: 500;
}

.referrals-tab-container .referred-users-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.referrals-tab-container .referred-user-card {
  background: #fafafa;
}

.referrals-tab-container .referred-user-info .user-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.referrals-tab-container .referred-user-info .user-index {
  background: #1890ff;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
}

.referrals-tab-container .referred-user-info .user-name {
  font-weight: 600;
  font-size: 14px;
  color: #262626;
}

.referrals-tab-container .referred-user-info .info-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 4px;
}

.referrals-tab-container .referred-user-info .info-row:last-child {
  margin-bottom: 0;
}

.referrals-tab-container .referred-user-info .info-row .label {
  color: #8c8c8c;
  min-width: 60px;
}

.referrals-tab-container .referred-user-info .info-row .value {
  color: #595959;
}

/* SNS 연동 탭 스타일 */
.sns-tab-content {
  padding: 16px;
}

.sns-search-section {
  margin-bottom: 24px;
}

.sns-search-section .section-title {
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.sns-search-section .section-title i {
  margin-right: 8px;
  color: #1890ff;
}

.sns-search-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.sns-result-section {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px;
}

.sns-result-section .section-title {
  margin: 0 0 16px 0;
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.sns-result-section .section-title i {
  margin-right: 8px;
  color: #52c41a;
}

.result-customer-id {
  font-size: 13px;
  font-weight: 400;
  color: #8c8c8c;
  margin-left: 8px;
}

.sns-empty-result {
  padding: 24px 0;
}

.sns-result-list {
  background: white;
  border-radius: 6px;
  padding: 12px;
}
</style>